/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useSnackbar } from "notistack";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import ConfirmationModal from "../../components/common/confirm/ConfirmationModal";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AdminLoader from "../../components/common/loader/AdminLoader";
import { useDispatch, useSelector } from "react-redux";
import { fetchRoles } from "../../store/slices/roleSlice";
import {
  addEmployee,
  deleteEmployee,
  fetchEmployees,
  updateEmployee,
} from "./../../store/slices/employeeSlice";
import { setCurrentPageTitle } from "../../store/slices/pageSlice";
import { fetchServiceTypes } from "../../store/slices/serviceTypesSlice";
import { ArrowDropDown, ArrowDropUp } from "@mui/icons-material";
import { getEmpDesignation } from "../../utils/EmpDesignation";
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { fetchGroups } from "../../store/slices/groupSlice";
import { fetchCompanyProfiles } from "../../store/slices/companyProfileSlice";
import { SquarePen, Trash2, EllipsisVertical } from "lucide-react";
import { MdSearch } from "react-icons/md";
import PermIdentityOutlinedIcon from '@mui/icons-material/PermIdentityOutlined';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import EngineeringOutlinedIcon from '@mui/icons-material/EngineeringOutlined';
import PeopleIcon from '@mui/icons-material/People';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShieldIcon from '@mui/icons-material/Shield';
import TableChartIcon from '@mui/icons-material/TableChart';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BarChartIcon from '@mui/icons-material/BarChart';
import WorkIcon from '@mui/icons-material/Work';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SpeedIcon from '@mui/icons-material/Speed';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SchoolIcon from '@mui/icons-material/School';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import InventoryIcon from "@mui/icons-material/Inventory";
import SettingsIcon from "@mui/icons-material/Settings";

// Support access preset used by support admin approval UI.
// mappedFeature values are compact submodule keys sent to backend.
export const SUPPORT_ACCESS_MODULES = [
  {
    id: "lead-tracker",
    name: "Lead tracker",
    subModules: [
      { id: "support-addlead", name: "Add lead", mappedFeature: "addlead", route: "add-lead" },
      { id: "support-leadreport", name: "Lead Reports", mappedFeature: "leadreport", route: "lead-report" },
      { id: "support-followuptable", name: "Follow-up table", mappedFeature: "followuptable", route: "follow-up-table" },
    ],
  },
  {
    id: "dashboard-tracker",
    name: "Dashboard tracker",
    subModules: [
      { id: "support-leadsource", name: "Lead Source", mappedFeature: "leadsource", route: "lead-source" },
      { id: "support-potentialstatus", name: "Potential Status", mappedFeature: "potentialstatus", route: "potential-status" },
      { id: "support-leadconversionrate", name: "Lead Conversion Rate", mappedFeature: "leadconversionrate", route: "lead-conversion-rate" },
      { id: "support-probabilitystatus", name: "Probability Status", mappedFeature: "probabilitystatus", route: "probability-status" },
      { id: "support-historicaldata", name: "Historical Data", mappedFeature: "historicaldata", route: "historical-data" },
      { id: "support-revenuedistribution", name: "Revenue Distribution", mappedFeature: "revenuedistribution", route: "revenue-distribution" },
    ],
  },
  {
    id: "operation-tracker",
    name: "Operation tracker",
    subModules: [
      { id: "support-salesprojects", name: "Sales Projects", mappedFeature: "salesprojects", route: "sales-projects" },
      { id: "support-taskinhand", name: "Task In Hand", mappedFeature: "taskinhand", route: "task-in-hand" },
      { id: "support-utilizationreport", name: "Utilization Report", mappedFeature: "utilizationreport", route: "utilization-report" },
    ],
  },
  {
    id: "settings",
    name: "Setting",
    subModules: [
      { id: "support-addgroup", name: "Add Group", mappedFeature: "addgroup", route: "add-group" },
      { id: "support-status", name: "Status", mappedFeature: "status", route: "status" },
      { id: "support-source", name: "Source", mappedFeature: "source", route: "source" },
      { id: "support-leadtype", name: "Lead Type", mappedFeature: "leadtype", route: "lead-type" },
      { id: "support-inventory", name: "Inventory", mappedFeature: "inventory", route: "inventory" },
      { id: "support-clientrating", name: "Client ratings", mappedFeature: "clientratingdashboardl3", route: "client-ratings" },
      { id: "support-assembly", name: "Assembly", mappedFeature: "assembly", route: "assembly" },
      { id: "support-subparts", name: "Subparts", mappedFeature: "subparts", route: "subparts" },
      { id: "support-hourlyrates", name: "Hourly rates", mappedFeature: "hourlyrates", route: "hourly-rates" },
      { id: "support-gandatax", name: "G&A tax", mappedFeature: "gandatax", route: "g-a-tax" },
      { id: "support-tax", name: "Tax", mappedFeature: "tax", route: "tax" },
    ],
  },
];

export const SUPPORT_ACCESS_FEATURE_PRESET = SUPPORT_ACCESS_MODULES.flatMap((module) =>
  module.subModules.map((sub) => ({
    ...sub,
    moduleId: module.id,
    moduleName: module.name,
  }))
);

const MY_TRAININGS_MODULE = {
  id: "my-trainings-module",
  name: "My trainings",
  icon: SchoolIcon,
  subFeatures: [
    { id: "my-trainings-module-sub", name: "My trainings", mappedFeature: "MY TRAININGS" },
  ],
};

const MY_TRAININGS_HIDDEN_MODULE_IDS = new Set([
  MY_TRAININGS_MODULE.id,
  "my-trainings",
  "finance-my-trainings",
]);

const hideMyTrainingsFromStructure = (structure = []) =>
  structure
    .filter((module) => !MY_TRAININGS_HIDDEN_MODULE_IDS.has(module.id))
    .map((module) => ({
      ...module,
      subFeatures: (module.subFeatures || []).filter(
        (sf) => sf.mappedFeature !== "MY TRAININGS"
      ),
    }))
    .filter((module) => (module.subFeatures || []).length > 0);

// Process Guide module - can be assigned from Role & Permissions.
const PROCESS_GUIDE_MODULE = {
  id: "process-guide",
  name: "Process Guide",
  icon: MenuBookOutlinedIcon,
  subFeatures: [
    { id: "process-guide-assistance", name: "Process guide assistance", mappedFeature: "PROCESS GUIDE ASSISTANCE" },
  ],
};

const Employees = () => {
  const dispatch = useDispatch();

  // Initialize access levels from backend featureAccess when editing
  // FEATURE_STRUCTURE is static so it's safe to omit it from deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const isDirector = localStorage.getItem("auth") === "L1";
    dispatch(setCurrentPageTitle(isDirector ? "Employee List" : "Add Employees"));
  }, [dispatch]);

  const [isLoading, SetisLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [showLogout, setshowlogout] = useState(false);
  const popupRef = useRef(null);
  const [servicetype, setSelectedServices] = useState("");
  const { serviceTypesRedux } = useSelector((state) => state.serviceTypesRedux);
  const { employees } = useSelector((state) => state.employees);
  const SuperUser = localStorage.getItem("auth");
  // const { group, lastFetch } = useSelector((state) => state.group);
  const { group } = useSelector((state) => state.group);
  const { profiles } = useSelector((state) => state.companyProfile);
  const subscriptionPackage = profiles?.[0]?.subscriptionpackage || "";
  console.log("Employees group", group)
  const [sortedProjects, setSortedProjects] = useState([]);
  const [accessList, setAccessList] = useState([]);
  const [featureAccessLevels, setFeatureAccessLevels] = useState({});
  const [featureSaveForFuture, setFeatureSaveForFuture] = useState({});
  const normalizeAccessLevelValue = useCallback((value) => {
    const normalized = String(value || "").trim().toLowerCase().replace(/[_-]/g, " ");
    if (normalized === "full access" || normalized === "fullaccess") return "Full Access";
    if (normalized === "view only" || normalized === "viewonly") return "View Only";
    if (normalized === "no access" || normalized === "noaccess") return "No Access";
    return "No Access";
  }, []);
  const normalizeSaveForFutureValue = useCallback((value) => {
    if (typeof value === "boolean") return value;
    const normalized = String(value || "").trim().toLowerCase();
    return normalized === "yes" || normalized === "true";
  }, []);
  const [expandedAdminModules, setExpandedAdminModules] = useState({
    "lead-tracker": true,
    "follow-up-table-module": true,
    "potential-status": true,
    "probability-status": true,
    "lead-source": true,
    "lead-conversion-rate": true,
    "historical-data": true,
    "revenue-distribution": true,
    "top-10-client": true,
    "revenue": true,
    "profit-and-loss-tracker": true,
    "gross-profit-tracker": true,
    "l4-operation-tracker": true,
    "l4-project-tracker": true,
    "operation-tracker": true,
    "l4-profit-and-loss-tracker": true,
    "l4-operations-rates": true,
    "add-group-module": true,
    "lead-type-module": true,
    "status-module": true,
    "source-module": true,
    "process-guide": true,
    "my-trainings-module": true,
  });
  console.log("gsdgsdg", accessList)

  // Feature structure for Role & Permissions
  const FEATURE_STRUCTURE = [
    {
      id: "follow-up-table",
      name: "FOLLOW-UP-TABLE",
      icon: TableChartIcon,
      subFeatures: [
        { id: "follow-up-table-sub", name: "FOLLOW-UP-TABLE", mappedFeature: "FOLLOW-UP-TABLE" }
      ]
    },
    {
      id: "potential-status",
      name: "POTENTIAL STATUS",
      icon: AssessmentIcon,
      subFeatures: [
        { id: "potential-status-sub", name: "POTENTIAL STATUS", mappedFeature: "POTENTIAL STATUS" }
      ]
    },
    {
      id: "probability-status",
      name: "PROBABILITY STATUS",
      icon: AssessmentIcon,
      subFeatures: [
        { id: "probability-status-sub", name: "PROBABILITY STATUS", mappedFeature: "PROBABILITY STATUS" }
      ]
    },
    {
      id: "lead-source",
      name: "LEAD SOURCE",
      icon: PeopleIcon,
      subFeatures: [
        { id: "lead-source-sub", name: "LEAD SOURCE", mappedFeature: "LEAD SOURCE" }
      ]
    },
    {
      id: "lead-conversion-rate",
      name: "LEAD CONVERSION RATE",
      icon: TrendingUpIcon,
      subFeatures: [
        { id: "lead-conversion-rate-sub", name: "LEAD CONVERSION RATE", mappedFeature: "LEAD CONVERSION RATE" }
      ]
    },
    {
      id: "historical-data",
      name: "HISTORICAL DATA",
      icon: BarChartIcon,
      subFeatures: [
        { id: "historical-data-sub", name: "HISTORICAL DATA", mappedFeature: "HISTORICAL DATA" }
      ]
    },
    {
      id: "revenue-distribution",
      name: "REVENUE DISTRIBUTION",
      icon: TrendingUpIcon,
      subFeatures: [
        { id: "revenue-distribution-sub", name: "REVENUE DISTRIBUTION", mappedFeature: "REVENUE DISTRIBUTION" }
      ]
    },
    {
      id: "my-request",
      name: "MY REQUEST",
      icon: RequestQuoteIcon,
      subFeatures: [
        { id: "my-request-sub", name: "MY REQUEST", mappedFeature: "MY REQUEST" }
      ]
    },
    {
      id: "task-in-hand",
      name: "TASK IN HAND",
      icon: WorkIcon,
      subFeatures: [
        { id: "task-in-hand-sub", name: "TASK IN HAND", mappedFeature: "TASK IN HAND" }
      ]
    },
    {
      id: "utilisation-report",
      name: "UTILISATION REPORT",
      icon: BarChartIcon,
      subFeatures: [
        { id: "utilisation-report-sub", name: "UTILISATION REPORT", mappedFeature: "UTILISATION REPORT" }
      ]
    },
    {
      id: "profit-and-loss",
      name: "PROFIT AND LOSS",
      icon: TrendingUpIcon,
      subFeatures: [
        { id: "profit-and-loss-sub", name: "PROFIT AND LOSS", mappedFeature: "PROFIT AND LOSS" }
      ]
    },
    {
      id: "ga-and-tax",
      name: "G&A and Tax",
      icon: TrendingUpIcon,
      subFeatures: [
        { id: "ga-and-tax-sub", name: "G&A and Tax", mappedFeature: "G&A AND TAX" }
      ]
    },
    {
      id: "gross-profit",
      name: "GROSS PROFIT",
      icon: TrendingUpIcon,
      subFeatures: [
        { id: "gross-profit-sub", name: "GROSS PROFIT", mappedFeature: "GROSS PROFIT" }
      ]
    },
    {
      id: "net-margin",
      name: "NET MARGIN",
      icon: TrendingUpIcon,
      subFeatures: [
        { id: "net-margin-sub", name: "NET MARGIN", mappedFeature: "NET MARGIN" }
      ]
    },
    {
      id: "my-projects",
      name: "MY PROJECTS",
      icon: AccountTreeIcon,
      subFeatures: [
        { id: "my-projects-sub", name: "MY PROJECTS", mappedFeature: "MY PROJECTS" }
      ]
    },
    {
      id: "approved-hrs",
      name: "APPROVED HRS",
      icon: AccessTimeIcon,
      subFeatures: [
        { id: "approved-hrs-sub", name: "APPROVED HRS", mappedFeature: "APPROVED HRS" }
      ]
    },
    {
      id: "productivity",
      name: "PRODUCTIVITY",
      icon: SpeedIcon,
      subFeatures: [
        { id: "productivity-sub", name: "PRODUCTIVITY", mappedFeature: "PRODUCTIVITY" }
      ]
    },
    {
      id: "projects-time-entries",
      name: "PROJECTS & TIME ENTRIES",
      icon: CalendarTodayIcon,
      subFeatures: [
        { id: "projects-time-entries-sub", name: "PROJECTS & TIME ENTRIES", mappedFeature: "PROJECTS & TIME ENTRIES" }
      ]
    },
    {
      id: "my-trainings",
      name: "MY TRAININGS",
      icon: SchoolIcon,
      subFeatures: [
        { id: "my-trainings-sub", name: "MY TRAININGS", mappedFeature: "MY TRAININGS" }
      ]
    }
  ];

  const createAdminSubMenus = (moduleId, moduleKey) => [
    {
      id: `${moduleId}-graphs`,
      name: "Graphs",
      mappedFeature: `${moduleKey} GRAPHS`,
    },
    {
      id: `${moduleId}-download`,
      name: "Download",
      mappedFeature: `${moduleKey} DOWNLOAD`,
    },
    {
      id: `${moduleId}-filter`,
      name: "Filter",
      mappedFeature: `${moduleKey} FILTER`,
    },
  ];

  // Admin (L0) feature structure: modules with sub-menus (only sub-menus have access level & save for future)
  const ADMIN_FEATURE_STRUCTURE = [
    {
      id: "lead-tracker",
      name: "Lead Tracker",
      icon: TableChartIcon,
      subFeatures: [
        { id: "lead-tracker-add-lead", name: "Add lead", mappedFeature: "ADD LEAD" },
        { id: "lead-tracker-edit-lead", name: "Edit lead", mappedFeature: "EDIT LEAD" },
        { id: "lead-tracker-lead-reports", name: "Lead Reports", mappedFeature: "LEAD REPORTS" },
        { id: "lead-tracker-import-leads", name: "Import leads", mappedFeature: "IMPORT LEADS" },
        { id: "lead-tracker-download-leads", name: "Download Template", mappedFeature: "DOWNLOAD TEMPLATE" },
        { id: "lead-tracker-attachments", name: "Attachments", mappedFeature: "ATTACHMENTS" },
      ]
    },
    {
      id: "follow-up-table-module",
      name: "Follow-up table",
      icon: TableChartIcon,
      subFeatures: [
        { id: "follow-up-table-module-main", name: "Follow-up table", mappedFeature: "FOLLOW-UP-TABLE" },
      ],
    },
    {
      id: "potential-status",
      name: "Potential Status",
      icon: AssessmentIcon,
      subFeatures: createAdminSubMenus("potential-status", "POTENTIAL STATUS"),
    },
    {
      id: "probability-status",
      name: "Probability Status",
      icon: AssessmentIcon,
      subFeatures: createAdminSubMenus("probability-status", "PROBABILITY STATUS"),
    },
    {
      id: "lead-source",
      name: "Lead Source",
      icon: PeopleIcon,
      subFeatures: createAdminSubMenus("lead-source", "LEAD SOURCE"),
    },
    {
      id: "lead-conversion-rate",
      name: "Lead Conversion Rate",
      icon: TrendingUpIcon,
      // Only Graphs + Filter (no Download) for Lead Conversion Rate
      subFeatures: [
        {
          id: "lead-conversion-rate-graphs",
          name: "Graphs",
          mappedFeature: "LEAD CONVERSION RATE GRAPHS",
        },
        {
          id: "lead-conversion-rate-filter",
          name: "Filter",
          mappedFeature: "LEAD CONVERSION RATE FILTER",
        },
      ],
    },
    {
      id: "historical-data",
      name: "Historical Data",
      icon: BarChartIcon,
      subFeatures: createAdminSubMenus("historical-data", "HISTORICAL DATA"),
    },
    {
      id: "revenue-distribution",
      name: "Revenue Distribution",
      icon: TrendingUpIcon,
      // Only Graphs + Filter (no Download) for Revenue Distribution
      subFeatures: [
        {
          id: "revenue-distribution-graphs",
          name: "Graphs",
          mappedFeature: "REVENUE DISTRIBUTION GRAPHS",
        },
        {
          id: "revenue-distribution-filter",
          name: "Filter",
          mappedFeature: "REVENUE DISTRIBUTION FILTER",
        },
      ],
    },
    {
      id: "profit-and-loss-tracker",
      name: "Profit And Loss Tracker",
      icon: TrendingUpIcon,
      subFeatures: subscriptionPackage.toLowerCase().includes("erp")
        ? [
            { id: "profit-and-loss-tracker-table", name: "Profit And Loss Tracker", mappedFeature: "PROFIT AND LOSS TRACKER TABLE" },
            { id: "profit-and-loss-tracker-actual", name: "Actual", mappedFeature: "PROFIT AND LOSS TRACKER ACTUAL" },
          ]
        : [
            { id: "profit-and-loss-tracker-table", name: "Profit And Loss Tracker", mappedFeature: "PROFIT AND LOSS TRACKER TABLE" },
          ],
    },
    {
      id: "gross-profit-tracker",
      name: "Gross Profit Tracker",
      icon: TrendingUpIcon,
      subFeatures: [
        ...createAdminSubMenus("gross-profit-tracker", "GROSS PROFIT TRACKER").filter(
          (s) =>
            s.id !== "gross-profit-tracker-graphs" &&
            s.id !== "gross-profit-tracker-download"
        ),
        { id: "gross-profit-tracker-total-gross-profit-graph", name: "Total Gross Profit Graph", mappedFeature: "GROSS PROFIT TRACKER TOTAL GROSS PROFIT GRAPH" },
        { id: "gross-profit-tracker-total-gross-profit-table", name: "Total Gross Profit Table", mappedFeature: "GROSS PROFIT TRACKER TOTAL GROSS PROFIT TABLE" },
        { id: "gross-profit-tracker-ytd-gross-profit-graph", name: "YTD Gross Profit Graph", mappedFeature: "GROSS PROFIT TRACKER YTD GROSS PROFIT GRAPH" },
        { id: "gross-profit-tracker-ytd-gross-profit-table", name: "YTD Gross Profit Table", mappedFeature: "GROSS PROFIT TRACKER YTD GROSS PROFIT TABLE" },
        { id: "gross-profit-tracker-net-margin-graph", name: "Net Margin Graph", mappedFeature: "GROSS PROFIT TRACKER NET MARGIN GRAPH" },
        { id: "gross-profit-tracker-net-margin-table", name: "Net Margin Table", mappedFeature: "GROSS PROFIT TRACKER NET MARGIN TABLE" },
        { id: "gross-profit-tracker-ytd-net-margin-graph", name: "YTD Net Margin Graph", mappedFeature: "GROSS PROFIT TRACKER YTD NET MARGIN GRAPH" },
        { id: "gross-profit-tracker-ytd-net-margin-table", name: "YTD Net Margin Table", mappedFeature: "GROSS PROFIT TRACKER YTD NET MARGIN TABLE" },
      ],
    },
    {
      id: "hardware-features",
      name: "Hardware features",
      icon: InventoryIcon,
      subFeatures: [
        // Hardware "Settings" section in sidebar is driven by Assembly + Hardware parts permissions.
        // We use Assembly Add as the representative key and auto-apply same access to the rest on save.
        { id: "hardware-features-settings", name: "Settings", mappedFeature: "ASSEMBLY ADD ASSEMBLY" },
      ],
    },
    {
      id: "inventory",
      name: "Inventory",
      icon: InventoryIcon,
      subFeatures: [
        { id: "inventory-add", name: "Add inventory", mappedFeature: "INVENTORY ADD INVENTORY" },
        { id: "inventory-edit", name: "Edit inventory", mappedFeature: "INVENTORY EDIT INVENTORY" },
      ],
    },
    {
      id: "assembly",
      name: "Assembly",
      icon: AccountTreeIcon,
      subFeatures: [
        { id: "assembly-add", name: "Add assembly", mappedFeature: "ASSEMBLY ADD ASSEMBLY" },
        { id: "assembly-edit", name: "Edit assembly", mappedFeature: "ASSEMBLY EDIT ASSEMBLY" },
        { id: "assembly-download", name: "Download assembly", mappedFeature: "ASSEMBLY DOWNLOAD ASSEMBLY" },
        { id: "assembly-upload", name: "Upload assembly", mappedFeature: "ASSEMBLY UPLOAD ASSEMBLY" },
      ],
    },
    {
      id: "hardware-parts",
      name: "Hardware parts",
      icon: TableChartIcon,
      subFeatures: [
        { id: "hardware-parts-add", name: "Add hardware parts", mappedFeature: "HARDWARE PARTS ADD HARDWARE PARTS" },
        { id: "hardware-parts-edit", name: "Edit hardware parts", mappedFeature: "HARDWARE PARTS EDIT HARDWARE PARTS" },
        { id: "hardware-parts-download", name: "Download hardware parts", mappedFeature: "HARDWARE PARTS DOWNLOAD HARDWARE PARTS" },
        { id: "hardware-parts-upload", name: "Upload hardware parts", mappedFeature: "HARDWARE PARTS UPLOAD HARDWARE PARTS" },
      ],
    },
    {
      id: "sales-settings",
      name: "Settings",
      icon: SettingsIcon,
      subFeatures: [
        { id: "sales-settings-client-ratings", name: "Client ratings", mappedFeature: "CLIENT RATINGS CREATE CLIENT RATING" },
        { id: "sales-settings-add-member", name: "Add member", mappedFeature: "ADD MEMBER ADD MEMBER" },
        { id: "sales-settings-hourly-rates-expenses", name: "Hourly rates & expenses", mappedFeature: "SALES HOURLY RATES ADD HOURLY RATES" },
        { id: "sales-settings-ga-tax-rates", name: "G&A tax and rates", mappedFeature: "TAX RATES G&A TAX" },
        { id: "sales-settings-quotation-template", name: "Quotation template", mappedFeature: "QUOTATION TEMPLATE CREATE QUOTATION TEMPLATE" },
        { id: "sales-settings-client-master", name: "Client master", mappedFeature: "CLIENT MASTER EDIT CLIENT DATA" },
      ],
    },
    {
      id: "wizard",
      name: "Wizard",
      icon: WorkIcon,
      subFeatures: [
        { id: "wizard-feature", name: "Wizard feature", mappedFeature: "WIZARD WIZARD FEATURE" },
      ],
    },
    {
      id: "manager-my-management",
      name: "My management",
      icon: WorkIcon,
      subFeatures: [
        { id: "l4-operation-tracker-my-management", name: "My management", mappedFeature: "MY MANAGEMENT" },
      ],
    },
    {
      id: "manager-my-assignments",
      name: "My assignments",
      icon: WorkIcon,
      subFeatures: [
        { id: "l4-operation-tracker-my-assignment", name: "My assignments", mappedFeature: "MY ASSIGNEMENT" },
      ],
    },
    {
      id: "project-tracker",
      name: "Project tracker",
      icon: AccountTreeIcon,
      subFeatures: [
        { id: "project-tracker-my-projects", name: "My projects", mappedFeature: "MY PROJECTS" },
        { id: "project-tracker-approved-hrs", name: "Approved Hrs", mappedFeature: "APPROVED HRS" },
        { id: "project-tracker-productivity", name: "Productivity", mappedFeature: "PRODUCTIVITY" },
        { id: "project-tracker-projects-time-entries", name: "Projects & Time Entries", mappedFeature: "PROJECTS & TIME ENTRIES" },
        { id: "project-tracker-project-request", name: "Project request", mappedFeature: "MY REQUEST" },
        { id: "project-tracker-task-in-hand", name: "Tasks In Hand", mappedFeature: "TASK IN HAND" },
        { id: "project-tracker-utilization-report", name: "Utilization Report", mappedFeature: "UTILISATION REPORT" },
        { id: "project-tracker-my-trainings", name: "My trainings", mappedFeature: "MY TRAININGS" },
      ],
    },
  ];

  // Sales person (L3) only: extra modules shown below Gross Profit Tracker
  const SALES_PERSON_EXTRA_MODULES = [
    {
      id: "client-ratings",
      name: "Client Ratings",
      icon: AssessmentIcon,
      subFeatures: [
        { id: "client-ratings-create", name: "Create Client Rating", mappedFeature: "CLIENT RATINGS CREATE CLIENT RATING" },
        { id: "client-ratings-download", name: "Download Client Rating", mappedFeature: "CLIENT RATINGS DOWNLOAD CLIENT RATING" },
      ],
    },
    {
      id: "tax-rates",
      name: "Tax Rates",
      icon: BarChartIcon,
      subFeatures: [
        { id: "tax-rates-ga-tax", name: "G&A Tax", mappedFeature: "TAX RATES G&A TAX" },
        { id: "tax-rates-tax", name: "Tax", mappedFeature: "TAX RATES TAX" },
      ],
    },
    {
      id: "quotation-template",
      name: "Quotation Template",
      icon: RequestQuoteIcon,
      subFeatures: [
        { id: "quotation-template-create", name: "Create Quotation Template", mappedFeature: "QUOTATION TEMPLATE CREATE QUOTATION TEMPLATE" },
        { id: "quotation-template-edit", name: "Edit Quotation Template", mappedFeature: "QUOTATION TEMPLATE EDIT QUOTATION TEMPLATE" },
        { id: "quotation-template-download", name: "Download Quotation Template", mappedFeature: "QUOTATION TEMPLATE DOWNLOAD QUOTATION TEMPLATE" },
      ],
    },
    {
      id: "client-master",
      name: "Client Master",
      icon: PeopleIcon,
      subFeatures: [
        { id: "client-master-edit", name: "Edit Client Data", mappedFeature: "CLIENT MASTER EDIT CLIENT DATA" },
      ],
    },
    {
      id: "wizard",
      name: "Wizard",
      icon: WorkIcon,
      subFeatures: [
        { id: "wizard-feature", name: "Wizard Feature", mappedFeature: "WIZARD WIZARD FEATURE" },
      ],
    },
    // Add Member (Settings → Add Member) - visible for Sales Person (L3) when subscription is ERP
    {
      id: "add-member-module",
      name: "Add Member",
      icon: PeopleIcon,
      subFeatures: [
        { id: "add-member-add", name: "Add member", mappedFeature: "ADD MEMBER ADD MEMBER" },
        { id: "add-member-edit", name: "Edit member", mappedFeature: "ADD MEMBER EDIT MEMBER" },
        { id: "add-member-delete", name: "Delete member", mappedFeature: "ADD MEMBER DELETE MEMBER" },
      ],
    },
    // Sales Hourly Rates & Expenses (Settings → Hourly Rates & Expenses for Sales Person L3, ERP only)
    {
      id: "sales-hourly-rates-module",
      name: "Hourly Rates & Expenses",
      icon: AccessTimeIcon,
      subFeatures: [
        { id: "sales-hourly-rates-add", name: "Add hourly rates", mappedFeature: "SALES HOURLY RATES ADD HOURLY RATES" },
        { id: "sales-hourly-rates-edit", name: "Edit hourly rates", mappedFeature: "SALES HOURLY RATES EDIT HOURLY RATES" },
        { id: "sales-hourly-rates-delete", name: "Delete hourly rates", mappedFeature: "SALES HOURLY RATES DELETE HOURLY RATES" },
      ],
    },
    {
      id: "operation-tracker",
      name: "Operation Tracker",
      icon: TableChartIcon,
      subFeatures: [
        { id: "operation-tracker-project-tracker", name: "Project Request", mappedFeature: "MY REQUEST" },
        { id: "operation-tracker-tasks-in-hand", name: "Tasks in hand", mappedFeature: "TASK IN HAND" },
        { id: "operation-tracker-utilization-report", name: "Utilization report", mappedFeature: "UTILISATION REPORT" },
      ],
    },
  ];
  // Admin (L0) / Director (L1): Operation Tracker is part of the dashboard for CRM and ERP; always list it for access configuration (same sub-features; P&L/GP differences are handled elsewhere in this structure).
  const OPERATION_TRACKER_MODULE = SALES_PERSON_EXTRA_MODULES.find((m) => m.id === "operation-tracker");
  const ADMIN_AND_DIRECTOR_ACCESS_MODULES =
    OPERATION_TRACKER_MODULE
      ? [...ADMIN_FEATURE_STRUCTURE, OPERATION_TRACKER_MODULE]
      : ADMIN_FEATURE_STRUCTURE;
  const SALES_PRESALES_EXCLUDED_MODULE_IDS = new Set([
    "hardware-features",
    "inventory",
    "assembly",
    "hardware-parts",
    "manager-my-management",
    "manager-my-assignments",
    "project-tracker",
  ]);
  const SALES_PRESALES_VISIBLE_EXTRA_MODULE_IDS = new Set([
    "wizard",
    "operation-tracker",
  ]);
  const SALES_PRESALES_EXTRA_MODULES = SALES_PERSON_EXTRA_MODULES.filter((module) =>
    SALES_PRESALES_VISIBLE_EXTRA_MODULE_IDS.has(module.id)
  );
  const CRM_SALES_SETTINGS_EXCLUDED_SUBFEATURES = new Set([
    "sales-settings-add-member",
    "sales-settings-hourly-rates-expenses",
  ]);

  const isCrmPackage = !subscriptionPackage.toLowerCase().includes("erp");

  const SALES_PRESALES_FEATURE_STRUCTURE = [
    ...ADMIN_AND_DIRECTOR_ACCESS_MODULES.filter(
      (module) =>
        !SALES_PRESALES_VISIBLE_EXTRA_MODULE_IDS.has(module.id) &&
        !SALES_PRESALES_EXCLUDED_MODULE_IDS.has(module.id)
    ).map((module) => {
      if (isCrmPackage && module.id === "sales-settings") {
        return {
          ...module,
          subFeatures: module.subFeatures.filter(
            (sf) => !CRM_SALES_SETTINGS_EXCLUDED_SUBFEATURES.has(sf.id)
          ),
        };
      }
      return module;
    }),
    ...SALES_PRESALES_EXTRA_MODULES,
    MY_TRAININGS_MODULE,
  ];

  // Settings modules for Admin (L0) and Director (L1) - CRM and ERP.
  // Company Profile and Employees are always visible (not controlled by featureAccess),
  // so only Add Group, Lead Type, Status and Source are configurable here.
  const SETTINGS_MODULES_ADMIN = [
    {
      id: "add-group-module",
      name: "Add Group",
      icon: GroupsOutlinedIcon,
      subFeatures: [
        { id: "add-group-add", name: "Add group", mappedFeature: "ADD GROUP ADD GROUP" },
        { id: "add-group-edit", name: "Edit group", mappedFeature: "ADD GROUP EDIT GROUP" },
        { id: "add-group-delete", name: "Delete group", mappedFeature: "ADD GROUP DELETE GROUP" },
      ],
    },
    {
      id: "lead-type-module",
      name: "Lead Type",
      icon: AssessmentIcon,
      subFeatures: [
        { id: "lead-type-add", name: "Add Lead type", mappedFeature: "LEAD TYPE ADD LEAD TYPE" },
        { id: "lead-type-edit", name: "Edit Lead type", mappedFeature: "LEAD TYPE EDIT LEAD TYPE" },
        { id: "lead-type-delete", name: "Delete Lead type", mappedFeature: "LEAD TYPE DELETE LEAD TYPE" },
      ],
    },
    {
      id: "status-module",
      name: "Status",
      icon: AssessmentIcon,
      subFeatures: [
        { id: "status-add", name: "Add Status", mappedFeature: "STATUS ADD STATUS" },
        { id: "status-edit", name: "Edit Status", mappedFeature: "STATUS EDIT STATUS" },
        { id: "status-delete", name: "Delete Status", mappedFeature: "STATUS DELETE STATUS" },
      ],
    },
    {
      id: "source-module",
      name: "Source",
      icon: PeopleIcon,
      subFeatures: [
        { id: "source-add", name: "Add source", mappedFeature: "SOURCE ADD SOURCE" },
        { id: "source-edit", name: "Edit source", mappedFeature: "SOURCE EDIT SOURCE" },
        { id: "source-delete", name: "Delete source", mappedFeature: "SOURCE DELETE SOURCE" },
      ],
    },
  ];

  const FINANCE_FEATURE_STRUCTURE = [
    {
      id: "finance-profit-and-loss",
      name: "PROFIT AND LOSS",
      icon: TrendingUpIcon,
      subFeatures: [
        { id: "finance-profit-and-loss-sub", name: "PROFIT AND LOSS", mappedFeature: "PROFIT AND LOSS" },
      ],
    },
    {
      id: "finance-ga-and-tax",
      name: "G&A and Tax",
      icon: TrendingUpIcon,
      subFeatures: [
        { id: "finance-ga-and-tax-sub", name: "G&A and Tax", mappedFeature: "G&A AND TAX" },
      ],
    },
    {
      id: "finance-follow-up-table",
      name: "Follow-up table",
      icon: TableChartIcon,
      subFeatures: [
        { id: "finance-follow-up-table-sub", name: "Follow-up table", mappedFeature: "FOLLOW-UP-TABLE" },
      ],
    },
    {
      id: "finance-my-trainings",
      name: "My trainings",
      icon: SchoolIcon,
      subFeatures: [
        { id: "finance-my-trainings-sub", name: "My trainings", mappedFeature: "MY TRAININGS" },
      ],
    },
  ];

  const ADMIN_EXCLUDED_MODULE_IDS = new Set([
    "hardware-features",
    "inventory",
    "assembly",
    "hardware-parts",
    "sales-settings",
    "wizard",
    "manager-my-management",
    "manager-my-assignments",
    "project-tracker",
  ]);

  const getAdminDirectorFeatureStructure = (authLevel) => {
    const filteredModules = ADMIN_AND_DIRECTOR_ACCESS_MODULES.filter(
      (module) => !ADMIN_EXCLUDED_MODULE_IDS.has(module.id)
    );
    if (authLevel === "L0" || authLevel === "L1") {
      return [
        ...filteredModules,
        MY_TRAININGS_MODULE,
        ...(subscriptionPackage === "ERP" ? [PROCESS_GUIDE_MODULE] : []),
        ...SETTINGS_MODULES_ADMIN,
      ];
    }
    return [
      ...filteredModules,
      MY_TRAININGS_MODULE,
      ...(subscriptionPackage === "ERP" ? [PROCESS_GUIDE_MODULE] : []),
    ];
  };

  const MANAGER_FEATURE_STRUCTURE = [
    {
      id: "l4-manager-my-management",
      name: "My management",
      icon: WorkIcon,
      subFeatures: [
        { id: "l4-manager-my-management-sub", name: "My management", mappedFeature: "MY MANAGEMENT" },
      ],
    },
    {
      id: "l4-manager-my-assignments",
      name: "My assignments",
      icon: WorkIcon,
      subFeatures: [
        { id: "l4-manager-my-assignments-sub", name: "My assignments", mappedFeature: "MY ASSIGNEMENT" },
      ],
    },
    {
      id: "l4-project-tracker",
      name: "Project tracker",
      icon: AccountTreeIcon,
      subFeatures: [
        { id: "l4-project-tracker-my-projects", name: "My projects", mappedFeature: "MY PROJECTS" },
        { id: "l4-project-tracker-approved-hrs", name: "Approved Hrs", mappedFeature: "APPROVED HRS" },
        { id: "l4-project-tracker-productivity", name: "Productivity", mappedFeature: "PRODUCTIVITY" },
        { id: "l4-project-tracker-projects-time-entries", name: "Projects & Time Entries", mappedFeature: "PROJECTS & TIME ENTRIES" },
        { id: "l4-project-tracker-project-request", name: "Project request", mappedFeature: "MY REQUEST" },
        { id: "l4-project-tracker-task-in-hand", name: "Tasks In Hand", mappedFeature: "TASK IN HAND" },
        { id: "l4-project-tracker-utilization-report", name: "Utilization Report", mappedFeature: "UTILISATION REPORT" },
      ],
    },
    {
      id: "l4-profit-and-loss-tracker",
      name: "Profit and loss tracker",
      icon: TrendingUpIcon,
      subFeatures: [
        { id: "l4-profit-and-loss-tracker-table", name: "P & L table", mappedFeature: "PROFIT AND LOSS" },
      ],
    },
    {
      id: "l4-operations-rates",
      name: "Operations rates",
      icon: AccessTimeIcon,
      subFeatures: [
        { id: "l4-operations-rates-hourly", name: "Hourly Rates & Expenses", mappedFeature: "OPERATIONS RATES HOURLY RATES & EXPENSES" },
      ],
    },
    MY_TRAININGS_MODULE,
  ];

  const MANAGER_OPERATION_TRACKER_MODULE = {
    id: "l4-operation-tracker",
    name: "Operation Tracker",
    icon: TableChartIcon,
    subFeatures: [
      { id: "l4-operation-tracker-my-management", name: "My management", mappedFeature: "MY MANAGEMENT" },
      { id: "l4-operation-tracker-my-assignment", name: "My assignement", mappedFeature: "MY ASSIGNEMENT" },
    ],
  };

  const MANAGER_HARDWARE_EXTRA_MODULES = [
    {
      id: "l4-inventory",
      name: "Inventory",
      icon: TableChartIcon,
      subFeatures: [
        { id: "l4-inventory-add", name: "Add inventory", mappedFeature: "INVENTORY ADD INVENTORY" },
        { id: "l4-inventory-edit", name: "Edit inventory", mappedFeature: "INVENTORY EDIT INVENTORY" },
      ],
    },
    {
      id: "l4-assembly",
      name: "Assembly",
      icon: AccountTreeIcon,
      subFeatures: [
        { id: "l4-assembly-add", name: "Add assembly", mappedFeature: "ASSEMBLY ADD ASSEMBLY" },
        { id: "l4-assembly-edit", name: "Edit assembly", mappedFeature: "ASSEMBLY EDIT ASSEMBLY" },
        { id: "l4-assembly-download", name: "Download assembly", mappedFeature: "ASSEMBLY DOWNLOAD ASSEMBLY" },
        { id: "l4-assembly-upload", name: "Upload assembly", mappedFeature: "ASSEMBLY UPLOAD ASSEMBLY" },
      ],
    },
    {
      id: "l4-hardware-parts",
      name: "Hardware parts",
      icon: TableChartIcon,
      subFeatures: [
        { id: "l4-hardware-parts-add", name: "Add hardware parts", mappedFeature: "HARDWARE PARTS ADD HARDWARE PARTS" },
        { id: "l4-hardware-parts-edit", name: "Edit hardware parts", mappedFeature: "HARDWARE PARTS EDIT HARDWARE PARTS" },
        { id: "l4-hardware-parts-download", name: "Download hardware parts", mappedFeature: "HARDWARE PARTS DOWNLOAD HARDWARE PARTS" },
        { id: "l4-hardware-parts-upload", name: "Upload hardware parts", mappedFeature: "HARDWARE PARTS UPLOAD HARDWARE PARTS" },
      ],
    },
  ];

  const HARDWARE_EXCLUDED_SUBFEATURE_IDS = new Set([
    "l4-project-tracker-approved-hrs",
    "l4-project-tracker-projects-time-entries",
    "l4-project-tracker-task-in-hand",
    "l4-project-tracker-utilization-report",
  ]);

  const MANAGER_HARDWARE_FEATURE_STRUCTURE = [
    MANAGER_OPERATION_TRACKER_MODULE,
    ...MANAGER_FEATURE_STRUCTURE
      .filter((module) => module.id === "l4-project-tracker")
      .map((module) => ({
        ...module,
        subFeatures: module.subFeatures.filter(
          (sf) => !HARDWARE_EXCLUDED_SUBFEATURE_IDS.has(sf.id)
        ),
      })),
    // Include the existing P&L table module for Hardware Manager.
    // This reuses the same module that other manager roles use.
    ...MANAGER_FEATURE_STRUCTURE.filter(
      (module) => module.id === "l4-profit-and-loss-tracker"
    ),
    ...MANAGER_HARDWARE_EXTRA_MODULES,
    MY_TRAININGS_MODULE,
  ];

  const OPERATIONS_FEATURE_STRUCTURE_BASE = [
    {
      id: "l4-project-tracker",
      name: "Project tracker",
      icon: AccountTreeIcon,
      subFeatures: [
        { id: "l4-project-tracker-my-projects", name: "My projects", mappedFeature: "MY PROJECTS" },
        { id: "l4-project-tracker-approved-hrs", name: "Approved Hrs", mappedFeature: "APPROVED HRS" },
        { id: "l4-project-tracker-productivity", name: "Productivity", mappedFeature: "PRODUCTIVITY" },
        { id: "l4-project-tracker-projects-time-entries", name: "Projects & Time Entries", mappedFeature: "PROJECTS & TIME ENTRIES" },
        { id: "l4-project-tracker-project-request", name: "Project request", mappedFeature: "MY REQUEST" },
        { id: "l4-project-tracker-task-in-hand", name: "Tasks In Hand", mappedFeature: "TASK IN HAND" },
        { id: "l4-project-tracker-utilization-report", name: "Utilization Report", mappedFeature: "UTILISATION REPORT" },
      ],
    },
    MY_TRAININGS_MODULE,
  ];

  const getOperationsFeatureStructure = (serviceTypeValue) => {
    if ((serviceTypeValue || "").toLowerCase() === "hardware") {
      return OPERATIONS_FEATURE_STRUCTURE_BASE.map((module) => {
        if (module.id === "l4-project-tracker") {
          return {
            ...module,
            subFeatures: module.subFeatures.filter(
              (sf) => !HARDWARE_EXCLUDED_SUBFEATURE_IDS.has(sf.id)
            ),
          };
        }
        return module;
      });
    }
    return OPERATIONS_FEATURE_STRUCTURE_BASE;
  };

  const OPERATIONS_FEATURE_STRUCTURE = useMemo(
    () => getOperationsFeatureStructure(servicetype),
    [servicetype]
  );

  const getRolePermissionStructure = useCallback((roleValue, serviceTypeOverride) => {
    const effectiveServiceType = serviceTypeOverride !== undefined ? serviceTypeOverride : servicetype;

    const withoutProcessGuideModule = (structure = []) =>
      structure.filter((module) => module.id !== PROCESS_GUIDE_MODULE.id);

    if (roleValue === "project manager") {
      const isHardwareService = (effectiveServiceType || "").toLowerCase() === "hardware";
      const managerStructure = isHardwareService
        ? MANAGER_HARDWARE_FEATURE_STRUCTURE
        : MANAGER_FEATURE_STRUCTURE;
      return hideMyTrainingsFromStructure(withoutProcessGuideModule(managerStructure));
    }
    if (roleValue === "operations") {
      const opsStructure = serviceTypeOverride !== undefined
        ? getOperationsFeatureStructure(serviceTypeOverride)
        : OPERATIONS_FEATURE_STRUCTURE;
      return hideMyTrainingsFromStructure(withoutProcessGuideModule(opsStructure));
    }
    if (roleValue === "L2") {
      return hideMyTrainingsFromStructure(
        FINANCE_FEATURE_STRUCTURE.filter((module) => module.id !== "finance-my-trainings")
      );
    }
    if (roleValue === "L5") {
      return hideMyTrainingsFromStructure(
        FINANCE_FEATURE_STRUCTURE.filter(
          (module) =>
            module.id !== "finance-my-trainings" && module.id !== "finance-ga-and-tax"
        )
      );
    }
    if (roleValue === "L3") {
      return hideMyTrainingsFromStructure(withoutProcessGuideModule(SALES_PRESALES_FEATURE_STRUCTURE));
    }
    if (roleValue === "L1") {
      const directorStructure = getAdminDirectorFeatureStructure("L1").map((module) => {
        if (module.id === "profit-and-loss-tracker") {
          return {
            ...module,
            subFeatures: [
              ...module.subFeatures.filter(
                (sf) => sf.id !== "profit-and-loss-tracker-actual"
              ),
              { id: "profit-and-loss-tracker-quoted", name: "Quoted", mappedFeature: "PROFIT AND LOSS TRACKER QUOTED" },
            ],
          };
        }
        return module;
      });
      return hideMyTrainingsFromStructure(directorStructure);
    }
    return hideMyTrainingsFromStructure(getAdminDirectorFeatureStructure("L0"));
  }, [servicetype, subscriptionPackage, OPERATIONS_FEATURE_STRUCTURE, MANAGER_HARDWARE_FEATURE_STRUCTURE, SALES_PRESALES_FEATURE_STRUCTURE, FINANCE_FEATURE_STRUCTURE]);

  const [allServiceTypes, setAllServiceTypes] = useState([]);
  console.log("Employees allServiceTypes", allServiceTypes)

  const LEAD_REPORTS_FEATURE_ID = "lead-tracker-lead-reports";
  const LEAD_REPORTS_FILTER_FEATURE_ID = "lead-tracker-lead-reports-filter";

  useEffect(() => {
    if (!group || group.length === 0) {
      setAllServiceTypes([]);
      return;
    }

    const uniqueServices = [
      ...new Set(
        group.flatMap(group => group.servicetype || [])
      )
    ];

    setAllServiceTypes(uniqueServices);
  }, [group]);

  // Handle access level change for sub-feature
  const handleAccessLevelChange = (subFeatureId, mappedFeature, accessLevel) => {
    // "Lead Reports Filter" is dependent on "Lead Reports":
    // when parent is No Access, child cannot be changed.
    if (
      subFeatureId === LEAD_REPORTS_FILTER_FEATURE_ID &&
      (featureAccessLevels[LEAD_REPORTS_FEATURE_ID] || "No Access") === "No Access"
    ) {
      return;
    }

    console.log("Updated Access:", { subFeatureId, mappedFeature, accessLevel });
    setFeatureAccessLevels((prev) => {
      const updated = { ...prev, [subFeatureId]: accessLevel };
      if (
        subFeatureId === LEAD_REPORTS_FEATURE_ID &&
        accessLevel === "No Access"
      ) {
        updated[LEAD_REPORTS_FILTER_FEATURE_ID] = "No Access";
      }
      console.log("Updated featureAccessLevels:", updated);
      return updated;
    });

    // Auto-sync Save for Future based on access level:
    // Full Access / View Only → Yes, No Access → No
    if (accessLevel === "Full Access" || accessLevel === "View Only") {
      setFeatureSaveForFuture((prev) => ({
        ...prev,
        [subFeatureId]: "Yes"
      }));
    } else {
      setFeatureSaveForFuture((prev) => ({
        ...prev,
        [subFeatureId]: "No",
        ...(subFeatureId === LEAD_REPORTS_FEATURE_ID
          ? { [LEAD_REPORTS_FILTER_FEATURE_ID]: "No" }
          : {})
      }));
    }

    // Update accessList based on access level
    if (accessLevel === "Full Access" || accessLevel === "View Only") {
      setAccessList((prev) => {
        if (!prev.includes(mappedFeature)) {
          return [...prev, mappedFeature];
        }
        return prev;
      });
    } else {
      setAccessList((prev) => prev.filter((f) => f !== mappedFeature));
    }
  };

  // Keep dependent child synced when parent is switched to No Access.
  useEffect(() => {
    const leadReportsAccess = featureAccessLevels[LEAD_REPORTS_FEATURE_ID] || "No Access";
    if (leadReportsAccess !== "No Access") return;

    if ((featureAccessLevels[LEAD_REPORTS_FILTER_FEATURE_ID] || "No Access") !== "No Access") {
      setFeatureAccessLevels((prev) => ({
        ...prev,
        [LEAD_REPORTS_FILTER_FEATURE_ID]: "No Access",
      }));
    }
    if ((featureSaveForFuture[LEAD_REPORTS_FILTER_FEATURE_ID] || "No") !== "No") {
      setFeatureSaveForFuture((prev) => ({
        ...prev,
        [LEAD_REPORTS_FILTER_FEATURE_ID]: "No",
      }));
    }
  }, [
    featureAccessLevels,
    featureSaveForFuture,
    LEAD_REPORTS_FEATURE_ID,
    LEAD_REPORTS_FILTER_FEATURE_ID,
  ]);

  // Handle save for future change (with validation against current access level)
  const handleSaveForFutureChange = (subFeatureId, value) => {
    const currentAccess = featureAccessLevels[subFeatureId] || "No Access";
    // Block invalid combinations
    if (currentAccess === "Full Access" && value === "No") return;
    if (currentAccess === "View Only" && value === "No") return;
    if (currentAccess === "No Access" && value === "Yes") return;
    setFeatureSaveForFuture((prev) => ({
      ...prev,
      [subFeatureId]: value
    }));
  };

  // Initialize access levels when accessList is reset (new employee)
  // Default: Full Access + Save for Future = Yes for all features
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (accessList.length === 0 && Object.keys(featureAccessLevels).length === 0) {
      const initialAccessLevels = {};
      const initialSaveForFuture = {};
      const initialAccessList = [];

      const initFeatures = (structures) => {
        structures.forEach((feature) => {
          feature.subFeatures.forEach((subFeature) => {
            initialAccessLevels[subFeature.id] = "Full Access";
            initialSaveForFuture[subFeature.id] = "Yes";
            if (subFeature.mappedFeature && !initialAccessList.includes(subFeature.mappedFeature)) {
              initialAccessList.push(subFeature.mappedFeature);
            }
          });
        });
      };

      initFeatures(FEATURE_STRUCTURE);
      initFeatures(ADMIN_FEATURE_STRUCTURE);
      initFeatures(SALES_PERSON_EXTRA_MODULES);
      initFeatures(MANAGER_FEATURE_STRUCTURE);
      initFeatures(MANAGER_HARDWARE_EXTRA_MODULES);
      initFeatures(OPERATIONS_FEATURE_STRUCTURE);
      initFeatures([{ subFeatures: PROCESS_GUIDE_MODULE.subFeatures }]);

      setFeatureAccessLevels(initialAccessLevels);
      setFeatureSaveForFuture(initialSaveForFuture);
      setAccessList(initialAccessList);
    }
  }, [accessList.length]);

  useEffect(() => {
    dispatch(fetchRoles());
    dispatch(fetchServiceTypes());
    dispatch(fetchEmployees())
    dispatch(fetchGroups());
    dispatch(fetchCompanyProfiles());
  }, [dispatch]);

  const [directorEmp, setDitectorEmp] = useState(null);

  useEffect(() => {
    const adminL0 = employees?.find((emp) => emp.EmpAuthLevel === "L0");
    const fallbackLocaleEmp = {
      countrycode: localStorage.getItem("countrycode") || "",
      region: localStorage.getItem("region") || "",
    };
    setDitectorEmp(adminL0 || fallbackLocaleEmp);
  }, [employees])

  const handleClickOutside = (event) => {
    if (popupRef.current && !popupRef.current.contains(event.target)) {
      setshowlogout(false); // Close the popup if clicked outside
    }
  };
  useEffect(() => {
    if (showLogout) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showLogout]);

  const [EmpFirstName, setEmpFirstName] = useState("");
  const [EmpLastName, setEmpLastName] = useState("");
  const [EmpPhone, setEmpPhone] = useState("");
  const [EmpEmail, setEmpEmail] = useState("");
  const [EmpAuthLevel, setEmpAuthLevel] = useState("");
  const [EmpGroups, setEmpGroups] = useState([]);
  const [AuthLevel, setAuthLevel] = useState("");
  const [showleveldown, setShowLeveldown] = useState(false);
  const [showleveldownL4Group, setShowLeveldownL4Group] = useState(false);
  const [customRoles, SetAssignedPeople] = useState([]);
  const [showServiceDropdown, setShowServiceDropdown] = useState(false);

  const isErpL4ManagerOrOps =
    subscriptionPackage === "ERP" &&
    (AuthLevel === "project manager" || AuthLevel === "operations");

  const erpL4ServiceTypesFromGroups = useMemo(() => {
    if (!isErpL4ManagerOrOps || !group?.length || !EmpGroups?.length) return [];
    const unique = new Set();
    EmpGroups.forEach((name) => {
      const g = group.find((gr) => gr.newGroupName === name);
      (g?.servicetype || []).forEach((st) => unique.add(st));
    });
    return [...unique];
  }, [isErpL4ManagerOrOps, group, EmpGroups]);

  // Remove excluded features when service type is "hardware" for Operations Person
  useEffect(() => {
    if (AuthLevel === "operations" && servicetype?.toLowerCase() === "hardware") {
      const excludedFeatures = [
        "APPROVED HRS",
        "PROJECTS & TIME ENTRIES",
        "TASK IN HAND",
        "UTILISATION REPORT"
      ];
      
      setAccessList((prev) => {
        const hasExcludedFeatures = excludedFeatures.some(feature => prev.includes(feature));
        if (hasExcludedFeatures) {
          return prev.filter(feature => !excludedFeatures.includes(feature));
        }
        return prev;
      });
    }
  }, [AuthLevel, servicetype]);

  // console.log("EmpGroups seleted",EmpGroups)
  const dropdownsRef = useRef(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // If dropdown is open and click is outside the dropdown AND not on the dropdown icon
      if (openDropdownId && dropdownsRef.current && !dropdownsRef.current.contains(event.target)) {
        // Also check if the click was on the ellipsis icon itself
        const clickedElement = event.target;
        const isEllipsisIcon = clickedElement.closest('.dropdown-toggle-icon');

        if (!isEllipsisIcon) {
          setOpenDropdownId(null);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdownId]);

  const dropdownRefLevel = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRefLevel.current &&
        !dropdownRefLevel.current.contains(event.target)
      ) {
        setShowLeveldown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleFirstName = (e) => {
    const value = e.target.value;

    if (/^[a-zA-Z0-9\s]*$/.test(value) && value.length <= 20) {
      setEmpFirstName(value);
    }
  };

  const handleLastName = (e) => {
    const value = e.target.value;

    if (/^[a-zA-Z0-9\s]*$/.test(value) && value.length <= 20) {
      setEmpLastName(value);
    }
  };

  const handleEmail = (e) => {
    // const value = e.target.value;
    const value = e.target.value.replace(/[^a-zA-Z0-9@.]/g, "");
    setEmpEmail(value);
    // }
  };


  // Filter service types based on selected groups
  useEffect(() => {
    if (EmpGroups.length > 0 && group && group.length > 0) {
      // Get all service types from selected groups
      const selectedGroupServiceTypes = new Set();

      EmpGroups.forEach((groupName) => {
        const selectedGroup = group.find((g) => g.newGroupName === groupName);
        if (selectedGroup && selectedGroup.servicetype && Array.isArray(selectedGroup.servicetype)) {
          selectedGroup.servicetype.forEach((st) => selectedGroupServiceTypes.add(st));
        }
      });

      // Filter service types to only include those from selected groups
      const filtered = serviceTypesRedux.filter((st) =>
        selectedGroupServiceTypes.has(st.ServiceType)
      );

      // Clear selected service type if it's not in the filtered list
      if (servicetype && filtered.length > 0) {
        const isServiceTypeValid = filtered.some((st) => st.ServiceType === servicetype);
        if (!isServiceTypeValid) {
          setSelectedServices("");
        }
      }
    } else {
      if (isErpL4ManagerOrOps && servicetype) {
        setSelectedServices("");
      }
    }
  }, [EmpGroups, group, serviceTypesRedux, servicetype, isErpL4ManagerOrOps]);

  const [editingEmployee, setEditingEmployee] = useState(null);
  const [GroupNames, setGroupNames] = useState([]);
  const [GroupAgainNames, setGroupAgainNames] = useState([]);

  const l4AddModeGroupsForPicker = useMemo(() => {
    if (isErpL4ManagerOrOps) return GroupAgainNames;
    return GroupAgainNames.filter(
      (g) => g.servicetype && g.servicetype.includes(servicetype)
    );
  }, [isErpL4ManagerOrOps, GroupAgainNames, servicetype]);

  console.log("Employees GroupAgainNames", GroupAgainNames)
  console.log("Employees editingEmployee", editingEmployee)

  useEffect(() => {
    setGroupNames(group);
    if (isErpL4ManagerOrOps) {
      setGroupAgainNames(group || []);
      return;
    }
    if (servicetype?.length > 0) {
      const matchedGroups = group?.filter((gr) => gr?.servicetype?.includes(servicetype))
      setGroupAgainNames(matchedGroups)
    } else {
      setGroupAgainNames(group)
    }
  }, [group, servicetype, isErpL4ManagerOrOps]);


  const [showDropdown, setShowDropdown] = useState(false);
  const [showTable, setShowTable] = useState(true); // Controls table visibility
  const resolveGroupName = (groupValue) => {
    if (!groupValue) return "";
    if (typeof groupValue === "object") {
      return (
        groupValue.newGroupName ||
        groupValue.groupName ||
        groupValue.GroupName ||
        groupValue.name ||
        groupValue._id ||
        ""
      );
    }
    const strValue = String(groupValue).trim();
    const matchedById = (group || []).find(
      (gr) => String(gr?._id || gr?.id) === strValue
    );
    if (matchedById) {
      return (
        matchedById.newGroupName ||
        matchedById.groupName ||
        matchedById.GroupName ||
        matchedById.name ||
        strValue
      );
    }
    return strValue;
  };
  const normalizeGroupsForUi = (groupsValue) =>
    Array.isArray(groupsValue)
      ? groupsValue.map((val) => resolveGroupName(val)).filter(Boolean)
      : [];
  const getEmployeeGroupNames = (employee) =>
    normalizeGroupsForUi(employee?.EmpGroups || employee?.groups || []);

  useEffect(() => {
    if (!group) return;

    const matchedGroups = group?.filter((gr) => gr?.servicetype?.includes(servicetype)) ?? [];
    // When editing or ERP L4 Manager/Ops, show ALL groups; otherwise when adding, filter by servicetype
    const groupsForList =
      editingEmployee || isErpL4ManagerOrOps
        ? (group || [])
        : (matchedGroups.length ? matchedGroups : group || []);

    // 1️⃣ Normalize all redux groups
    const formattedAllGroups = groupsForList.map((g, idx) =>
      typeof g === "string" ? { id: idx, newGroupName: g } : g
    );

    const serviceFiltered = formattedAllGroups;

    const employeeGroups = normalizeGroupsForUi(editingEmployee?.EmpGroups || []);

    // 2️⃣ Merge: keep redux groups, mark selected
    const merged = serviceFiltered.map((g) => ({
      ...g,
      selected: employeeGroups.includes(g.newGroupName),
    }));

    // 3️⃣ Add employee groups not already in merged
    const existingNames = new Set(merged.map((g) => g.newGroupName));
    const extraGroups = employeeGroups
      .filter((eg) => !existingNames.has(eg)) // only missing ones
      .map((eg, idx) => ({
        id: serviceFiltered?.length + idx + 1, // unique id
        newGroupName: eg,
        selected: true,
      }));

    const final = [...merged, ...extraGroups];
    setGroupNames(final);
  }, [group, editingEmployee, servicetype, isErpL4ManagerOrOps]);

  const editEmployee = (id) => {
    const employeeToEdit = l1employees.find((employee) => employee.id === id);
    setEditingEmployee(employeeToEdit);
    setShowTable(false); // Hide the table when editing starts

    // Ensure EmpGroups is set to employee's existing groups
    if (employeeToEdit && employeeToEdit.groups) {
      setEmpGroups(normalizeGroupsForUi(employeeToEdit.groups));
      setShowTable(false); // Hide the table when editing starts
    } else {
      setEmpGroups([]); // Reset if no groups
    }
  };

  useEffect(() => {
    if (editingEmployee) {
      console.log("gddfgdgeditingEmployee", editingEmployee)
      setEmpFirstName(editingEmployee.EmpFirstName);
      setEmpLastName(editingEmployee.EmpLastName);
      const phoneStr = String(editingEmployee.EmpPhone || "");
      const lastTenDigits = phoneStr.slice(-10);
      if (directorEmp?.countrycode === "+1") {
        setEmpPhone("1" + lastTenDigits);
      } else if (directorEmp?.countrycode === "+91") {
        setEmpPhone("91" + lastTenDigits);
      } else {
        setEmpPhone(lastTenDigits);
      }

      // setEmpPhone(editingEmployee.EmpPhone);
      setEmpEmail(editingEmployee.EmpEmail);
      
      // Handle featureAccess from backend (supports both old string[] and new object[] shapes)
      const backendFeatureAccess = Array.isArray(editingEmployee.featureAccess)
        ? editingEmployee.featureAccess
        : [];

      console.log("Raw backend featureAccess:", backendFeatureAccess);

      const featureNamesFromBackend = [];
      const backendAccessMap = {}; // canonicalFeatureName -> { accessLevel, saveForFuture }

      backendFeatureAccess.forEach((item) => {
        let rawName = "";
        let accessLevel = "Full Access";
        let saveForFuture = false;

        if (typeof item === "string" || typeof item === "number") {
          // Old schema – simple strings
          rawName = String(item);
        } else if (item && typeof item === "object") {
          // NEW SCHEMA: Prioritize featureName (what we're sending now)
          if (item.featureName) {
            rawName = item.featureName;
            accessLevel = normalizeAccessLevelValue(item.accessLevel);
            saveForFuture = normalizeSaveForFutureValue(item.saveForFuture);
          } else {
            // OLD SCHEMA: Try other name fields
            rawName =
              item.feature ||
              item.name ||
              "";

            // Fallback: handle String objects like {0:'M',1:'Y',...}
            if (!rawName) {
              const charKeys = Object.keys(item)
                .filter((k) => /^\d+$/.test(k))
                .sort((a, b) => Number(a) - Number(b));
              if (charKeys.length) {
                rawName = charKeys.map((k) => item[k]).join("");
              }
            }

            if (item.accessLevel !== undefined && item.accessLevel !== null) {
              accessLevel = normalizeAccessLevelValue(item.accessLevel);
            }
            if (item.saveForFuture !== undefined && item.saveForFuture !== null) {
              saveForFuture = normalizeSaveForFutureValue(item.saveForFuture);
            }
          }
        }

        const canonicalName = rawName.trim().toUpperCase();

        // Skip invalid entries (like "[OBJECT OBJECT]")
        if (canonicalName && canonicalName !== "[OBJECT OBJECT]") {
          featureNamesFromBackend.push(canonicalName);
          backendAccessMap[canonicalName] = {
            accessLevel,
            saveForFuture,
          };
        }
      });

      console.log("featureNamesFromBackend:", featureNamesFromBackend);
      console.log("backendAccessMap:", backendAccessMap);
      console.log("backendAccessMap keys:", Object.keys(backendAccessMap));

      // Filter out excluded features if service type is "hardware" for Manager/Operations Person
      const employeeServiceType = editingEmployee?.servicetype || "";
      const isManagerOrOps =
        editingEmployee.EmpAuthLevel === "L4" &&
        (editingEmployee.customRoles?.includes("project manager") ||
          editingEmployee.customRoles?.includes("operations"));

      let filteredFeatureNames = [...featureNamesFromBackend];

      if (isManagerOrOps && employeeServiceType?.toLowerCase() === "hardware") {
        const excludedFeatures = [
          "APPROVED HRS",
          "PROJECTS & TIME ENTRIES",
          "TASK IN HAND",
          "UTILISATION REPORT",
        ].map((f) => f.toUpperCase());
        filteredFeatureNames = filteredFeatureNames.filter(
          (feature) => !excludedFeatures.includes(feature)
        );
      }

      setAccessList(filteredFeatureNames);
      console.log("filteredFeatureNames (after hardware filter):", filteredFeatureNames);

      // Initialize access level and save-for-future state from backend data.
      // The access list UI is now common for every employee (same as Admin list),
      // so we must load state using that same structure.
      const initialAccessLevels = {};
      const initialSaveForFuture = {};
      const roleForPermissions = editingEmployee.customRoles?.includes("project manager")
        ? "project manager"
        : editingEmployee.customRoles?.includes("operations")
          ? "operations"
          : editingEmployee.EmpAuthLevel;
      const structureToLoad = getRolePermissionStructure(roleForPermissions, editingEmployee?.servicetype);

      const legacyKeyMap = {
        "OPERATIONS RATES HOURLY RATES & EXPENSES": "SALES HOURLY RATES ADD HOURLY RATES",
      };

      structureToLoad.forEach((module) => {
        module.subFeatures.forEach((subFeature) => {
          const canonicalMapped = subFeature.mappedFeature.trim().toUpperCase();
          let backendConfig = backendAccessMap[canonicalMapped];
          const isInList = filteredFeatureNames.includes(canonicalMapped);

          if (!backendConfig && legacyKeyMap[canonicalMapped]) {
            backendConfig = backendAccessMap[legacyKeyMap[canonicalMapped]];
          }

          if (backendConfig) {
            initialAccessLevels[subFeature.id] = normalizeAccessLevelValue(backendConfig.accessLevel);
            initialSaveForFuture[subFeature.id] = backendConfig.saveForFuture ? "Yes" : "No";
          } else if (isInList) {
            initialAccessLevels[subFeature.id] = "Full Access";
            initialSaveForFuture[subFeature.id] = "Yes";
          } else {
            initialAccessLevels[subFeature.id] = "No Access";
            initialSaveForFuture[subFeature.id] = "No";
          }
        });
      });

      setFeatureAccessLevels(initialAccessLevels);
      setFeatureSaveForFuture(initialSaveForFuture);

      console.log("initialAccessLevels:", initialAccessLevels);
      console.log("initialSaveForFuture:", initialSaveForFuture);
      
      // setAuthLevel(editingEmployee.EmpAuthLevel);
      setEmpGroups(normalizeGroupsForUi(editingEmployee.EmpGroups));
      SetAssignedPeople(editingEmployee.customRoles);
      setSelectedServices(editingEmployee?.servicetype || "")

      if (editingEmployee.EmpAuthLevel === "L4" && editingEmployee.customRoles?.includes("project manager")) {
        setAuthLevel("project manager")
        setEmpAuthLevel("L4")
      }
      if (editingEmployee.EmpAuthLevel === "L4" && editingEmployee.customRoles?.includes("operations")) {
        setAuthLevel("operations")
        setEmpAuthLevel("L4")
      }
      if (editingEmployee.EmpAuthLevel === "L0") {
        setAuthLevel("L0");
        setEmpAuthLevel("L0");
      }
      if (editingEmployee.EmpAuthLevel === "L3") {
        setEmpAuthLevel("L3")
      }
      if (
        editingEmployee.EmpAuthLevel === "L1" ||
        editingEmployee.EmpAuthLevel === "L2" ||
        editingEmployee.EmpAuthLevel === "L3" ||
        editingEmployee.EmpAuthLevel === "L5"
      ) {
        setAuthLevel(editingEmployee.EmpAuthLevel)
        setEmpAuthLevel(editingEmployee.EmpAuthLevel)
      }
    }

  }, [editingEmployee, directorEmp]);

  // Find the most recently created employee with the same role
  const getLastUserPermissionsForRole = useCallback((role, svcType) => {
    if (!employees || !employees.length || !role) return null;

    const matchingEmployees = employees.filter((emp) => {
      if (role === "project manager") {
        return emp.EmpAuthLevel === "L4" && emp.customRoles?.includes("project manager");
      }
      if (role === "operations") {
        return emp.EmpAuthLevel === "L4" && emp.customRoles?.includes("operations");
      }
      return emp.EmpAuthLevel === role;
    });

    if (!matchingEmployees.length) return null;

    // For L4 roles, prefer matching service type
    if (svcType && (role === "project manager" || role === "operations")) {
      const serviceMatched = matchingEmployees.filter(
        (emp) => emp.servicetype?.toLowerCase() === svcType.toLowerCase()
      );
      if (serviceMatched.length) {
        const sorted = [...serviceMatched].sort((a, b) => {
          const idA = a._id || a.id || "";
          const idB = b._id || b.id || "";
          return idA > idB ? -1 : idA < idB ? 1 : 0;
        });
        if (sorted[0]?.featureAccess?.length) return sorted[0];
      }
    }

    // Fall back to most recent employee of same role (sort by _id desc)
    const sorted = [...matchingEmployees].sort((a, b) => {
      const idA = a._id || a.id || "";
      const idB = b._id || b.id || "";
      return idA > idB ? -1 : idA < idB ? 1 : 0;
    });
    const lastUser = sorted[0];
    if (!lastUser?.featureAccess || !Array.isArray(lastUser.featureAccess) || !lastUser.featureAccess.length) {
      return null;
    }
    return lastUser;
  }, [employees]);

  // When role changes during new user creation, pre-fill permissions from last user of same role
  useEffect(() => {
    if (editingEmployee) return;
    if (!AuthLevel) return;

    const lastUser = getLastUserPermissionsForRole(AuthLevel, servicetype);
    const structure = getRolePermissionStructure(AuthLevel);
    if (!structure.length) return;

    if (lastUser && lastUser.featureAccess) {
      const backendFeatureAccess = Array.isArray(lastUser.featureAccess) ? lastUser.featureAccess : [];
      const backendAccessMap = {};

      backendFeatureAccess.forEach((item) => {
        let rawName = "";
        let accessLevel = "Full Access";
        let saveForFuture = false;

        if (typeof item === "string" || typeof item === "number") {
          rawName = String(item);
        } else if (item && typeof item === "object") {
          if (item.featureName) {
            rawName = item.featureName;
            accessLevel = normalizeAccessLevelValue(item.accessLevel);
            saveForFuture = normalizeSaveForFutureValue(item.saveForFuture);
          } else {
            rawName = item.feature || item.name || "";
            if (!rawName) {
              const charKeys = Object.keys(item).filter(k => /^\d+$/.test(k)).sort((a, b) => Number(a) - Number(b));
              if (charKeys.length) rawName = charKeys.map(k => item[k]).join("");
            }
            if (item.accessLevel !== undefined && item.accessLevel !== null) {
              accessLevel = normalizeAccessLevelValue(item.accessLevel);
            }
            if (item.saveForFuture !== undefined && item.saveForFuture !== null) {
              saveForFuture = normalizeSaveForFutureValue(item.saveForFuture);
            }
          }
        }

        const canonicalName = rawName.trim().toUpperCase();
        if (canonicalName && canonicalName !== "[OBJECT OBJECT]") {
          backendAccessMap[canonicalName] = { accessLevel, saveForFuture };
        }
      });

      const newAccessLevels = {};
      const newSaveForFuture = {};
      const newAccessList = [];

      const legacyKeyMap = {
        "OPERATIONS RATES HOURLY RATES & EXPENSES": "SALES HOURLY RATES ADD HOURLY RATES",
      };

      structure.forEach((module) => {
        module.subFeatures.forEach((subFeature) => {
          const canonicalMapped = subFeature.mappedFeature.trim().toUpperCase();
          let backendConfig = backendAccessMap[canonicalMapped];

          if (!backendConfig && legacyKeyMap[canonicalMapped]) {
            backendConfig = backendAccessMap[legacyKeyMap[canonicalMapped]];
          }

          if (backendConfig) {
            newAccessLevels[subFeature.id] = normalizeAccessLevelValue(backendConfig.accessLevel);
            newSaveForFuture[subFeature.id] = backendConfig.saveForFuture ? "Yes" : "No";
            if (normalizeAccessLevelValue(backendConfig.accessLevel) !== "No Access") {
              if (!newAccessList.includes(subFeature.mappedFeature)) {
                newAccessList.push(subFeature.mappedFeature);
              }
            }
          } else {
            newAccessLevels[subFeature.id] = "Full Access";
            newSaveForFuture[subFeature.id] = "Yes";
            if (!newAccessList.includes(subFeature.mappedFeature)) {
              newAccessList.push(subFeature.mappedFeature);
            }
          }
        });
      });

      setFeatureAccessLevels(newAccessLevels);
      setFeatureSaveForFuture(newSaveForFuture);
      setAccessList(newAccessList);
      console.log("Pre-filled permissions from last user:", lastUser?.EmpFirstName, lastUser?.EmpLastName, "role:", AuthLevel);
    } else {
      const defaultAccessLevels = {};
      const defaultSaveForFuture = {};
      const defaultAccessList = [];

      structure.forEach((module) => {
        module.subFeatures.forEach((subFeature) => {
          defaultAccessLevels[subFeature.id] = "Full Access";
          defaultSaveForFuture[subFeature.id] = "Yes";
          if (subFeature.mappedFeature && !defaultAccessList.includes(subFeature.mappedFeature)) {
            defaultAccessList.push(subFeature.mappedFeature);
          }
        });
      });

      setFeatureAccessLevels(defaultAccessLevels);
      setFeatureSaveForFuture(defaultSaveForFuture);
      setAccessList(defaultAccessList);
      console.log("No previous user for role:", AuthLevel, "- defaulting to Full Access");
    }
    // Intentionally omit getRolePermissionStructure / getLastUserPermissionsForRole: those callbacks
    // depend on feature-structure arrays recreated each render, so listing them re-ran this effect on
    // every state update and reset access radios back to prefill (often Full Access). Re-run only when
    // role, service, package, employee list, or add vs edit mode actually changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [AuthLevel, servicetype, subscriptionPackage, editingEmployee, employees]);

  // const resetForm = () => {
  //   setEmpFirstName("");
  //   setEmpLastName("");
  //   setEmpPhone("");
  //   setEmpEmail("");
  //   setAuthLevel("");
  //   setEmpGroups([]);
  //   setAccessList([])
  // };

  const resetForm = () => {
    setEmpFirstName("");
    setEmpLastName("");

    // Reset phone number based on directorEmp's countrycode
    if (directorEmp?.countrycode === "+1") {
      setEmpPhone("1"); // Start with US country code
    } else if (directorEmp?.countrycode === "+91") {
      setEmpPhone("91"); // Start with India country code
    } else {
      setEmpPhone(""); // Fallback
    }

    setEmpEmail("");
    setAuthLevel("");
    setEmpGroups([]);
    setAccessList([]);
    setFeatureAccessLevels({});
    setFeatureSaveForFuture({});
  };

  const [l1employees, setEmployees] = useState([]);

  const [searchQuery, setSearchQuery] = useState("");
  const [filteredEmployees, setFilteredEmployees] = useState([...employees].reverse());

  useEffect(() => {
    dispatch(fetchEmployees());
  }, [dispatch]);

  const DESIGNATION_ORDER = {
    "L0": 1,
    "L1": 2,
    "L3": 3,
    "L2": 4,
    "L5": 5,
    "L4-manager": 5,
    "L4-operations": 6,
  };

  const getDesignationRank = (employee) => {
    if (!employee) return 99;
    if (employee.EmpAuthLevel === "L4") {
      if (employee.customRoles?.includes("project manager")) return DESIGNATION_ORDER["L4-manager"];
      if (employee.customRoles?.includes("operations")) return DESIGNATION_ORDER["L4-operations"];
      return 7;
    }
    return DESIGNATION_ORDER[employee.EmpAuthLevel] || 99;
  };

  const sortByDesignation = (list) => {
    return [...list].sort((a, b) => getDesignationRank(a) - getDesignationRank(b));
  };

  useEffect(() => {
    if (employees) {
      console.log("Hehe", employees)
      const sorted = sortByDesignation(employees);
      setEmployees(sorted);
      setFilteredEmployees(sorted);
    }
  }, [employees]);

  useEffect(() => { }, [filteredEmployees]);


  const Submit = async (e) => {
    e.preventDefault();

    console.log("Gewegweg", EmpAuthLevel)
    if (
      !EmpFirstName ||
      !EmpLastName ||
      !EmpPhone ||
      !EmpEmail ||
      !EmpAuthLevel
    ) {
      enqueueSnackbar("Please Fill All The Fields!", { variant: "error" });
      return;
    }

    if (EmpAuthLevel === "L3" && (!EmpGroups || EmpGroups.length === 0)) {
      enqueueSnackbar(" Please Select At Least One Group For Sales Person !", {
        variant: "error",
      });
      return;
    }
    if (EmpAuthLevel === "L4" && !servicetype) {
      enqueueSnackbar(" Please Select Service Type !", {
        variant: "error",
      });
      return;
    }
    if (EmpAuthLevel === "L4" && (!EmpGroups || EmpGroups.length === 0)) {
      enqueueSnackbar(" Please Select At Least One Group !", {
        variant: "error",
      });
      return;
    }

    const lastTenDigits = EmpPhone.slice(-10);
    if (/^0{10}$/.test(lastTenDigits)) {
      enqueueSnackbar("Enter Valid Phone Number!", {
        variant: "error",
      });
      return;
    }
    const numericPhone = EmpPhone.replace(/\D/g, ""); // Removes non-numeric characters
    console.log("Phone after cleanup:", numericPhone);

    // CORRECTED VALIDATION LOGIC
    if (directorEmp?.countrycode === "+1") {
      // US/Canada: Country code +1, total digits should be 11 (1 + 10)
      // OR just check that it's exactly 10 digits after removing the +1 country code
      const usPhoneWithoutCountryCode = numericPhone.replace(/^1/, '');
      if (usPhoneWithoutCountryCode.length !== 10) {
        enqueueSnackbar("Phone Number Must Be 10 Digits", {
          variant: "error",
        });
        return;
      }
    } else if (directorEmp?.countrycode === "+91") {
      // India: Country code +91, total digits should be 12 (91 + 10)
      // OR just check that it's exactly 10 digits after removing the +91 country code
      const indiaPhoneWithoutCountryCode = numericPhone.replace(/^91/, '');
      if (indiaPhoneWithoutCountryCode.length !== 10) {
        enqueueSnackbar("Phone Number Must Be 10 Digits", {
          variant: "error",
        });
        return;
      }
    } else {
      // Default validation for other country codes
      if (numericPhone.length < 10) {
        enqueueSnackbar("Phone Number Must Be At Least 10 Digits!", {
          variant: "error",
        });
        return;
      }
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(EmpEmail)) {
      enqueueSnackbar("Please enter a valid email address!", { variant: "error" });
      return;
    }

    try {
      SetisLoading(true);

      // Backend expects EmpGroups as array of Group._id (ObjectId)
      const empGroupIds = (Array.isArray(EmpGroups) ? EmpGroups : [])
        .map((g) => {
          // Already an ObjectId string
          if (typeof g === "string" && /^[0-9a-fA-F]{24}$/.test(g)) return g;
          // Sometimes stored as object
          if (g && typeof g === "object") return g._id || g.id || g.value;
          // UI stores group names (newGroupName) -> map to _id
          if (typeof g === "string") {
            const matched = (group || []).find((gr) => gr?.newGroupName === g);
            return matched?._id || matched?.id;
          }
          return undefined;
        })
        .filter(Boolean);

      if (!editingEmployee) {
        // Dispatch addEmployee action
        await dispatch(
          addEmployee({
            EmpFirstName,
            EmpLastName,
            EmpPhone,
            EmpEmail,
            EmpAuthLevel,
            EmpGroups: empGroupIds,
            customRoles,
            servicetype,
            region: directorEmp?.region,
            countrycode: directorEmp?.countrycode,
            featureAccess: buildFeatureAccessPayload()
          })
        ).unwrap(); // Ensure we catch errors properly

        enqueueSnackbar("Employee Added Successfully!", { variant: "success" });
        dispatch(fetchEmployees());
      } else {
        const updatedData = {
          EmpFirstName,
          EmpLastName,
          EmpPhone,
          EmpEmail,
          EmpAuthLevel,
          EmpGroups: empGroupIds,
          customRoles,
          servicetype,
          region: directorEmp?.region,
          countrycode: directorEmp?.countrycode,
          featureAccess: buildFeatureAccessPayload()
        };
        await dispatch(
          updateEmployee({
            id: editingEmployee.id || editingEmployee._id,
            updatedData,
          })
        ).unwrap();

        enqueueSnackbar("Employee Details Updated Successfully!", {
          variant: "success",
        });
        dispatch(fetchEmployees());
        setShowTable(true);
        setEditingEmployee(null);
        SetAssignedPeople([]);
      }
      resetForm();
    } catch (error) {
      const errorMsg = error?.error || error?.message || (typeof error === "string" ? error : "Something went wrong!");
      console.error("Error updating employee:", error);
      enqueueSnackbar(errorMsg, { variant: "error" });
    }
    SetisLoading(false);
    setAuthLevel("");
    setEmpAuthLevel("")
    setSelectedServices("")
    setSearchQuery("")

  };

  const handleDeleteEmployee = async (id) => {
    SetisLoading(true);
    try {
      await dispatch(deleteEmployee(id)).unwrap();
      enqueueSnackbar("Employee deleted successfully!", { variant: "success" });
      dispatch(fetchEmployees());
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar("Failed to delete employee!", { variant: "error" });
    } finally {
      SetisLoading(false);
      setSearchQuery("")
    }
  };

  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    setIsEditing(!!editingEmployee);
  }, [editingEmployee]);

  const Cancel = (e) => {
    e.preventDefault();

    setEmpFirstName("");
    setEmpLastName("");
    setEmpEmail("");
    setEmpAuthLevel("");
    setEmpGroups([]);
    SetAssignedPeople([]);
    setIsEditing(false);
    setShowTable(true);
    setSelectedServices("")
    setAuthLevel("");
    setEmpAuthLevel("")
    setSearchQuery("")
    setEditingEmployee(null)
    setAccessList([]);
    setFeatureAccessLevels({});
    setFeatureSaveForFuture({});
    if (directorEmp?.countrycode === "+1") {
      setEmpPhone("1"); // Start with US country code
    } else if (directorEmp?.countrycode === "+91") {
      setEmpPhone("91"); // Start with India country code
    } else {
      setEmpPhone(""); // Fallback
    }
  };

  const [currentPage, setCurrentPage] = useState(1); // Track the current page

  const [itemsPerPage] = useState(10);

  const totalItems = sortedProjects?.length;
  const startIdx = (currentPage - 1) * itemsPerPage;
  const endIdx = startIdx + itemsPerPage;
  const totalPages = Math.ceil(sortedProjects?.length / itemsPerPage);
  const currentData = sortedProjects?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const handleSearchInputChange = (e) => {
    const value = e.target.value.replace(/[^a-zA-Z0-9 ]/g, "");
    setSearchQuery(value);
    filterEmployees(value);
    setCurrentPage(1);
  };

  const dropdownRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!dropdownRef.current?.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const dropdownRefLevelL4Group = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!dropdownRefLevelL4Group.current?.contains(event.target)) {
        setShowLeveldownL4Group(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const dropdownRefService = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRefService.current &&
        !dropdownRefService.current.contains(event.target)
      ) {
        setShowServiceDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [EmployeeToDelete, setEmployeeToDelete] = useState();
  const openDeleteModal = (id) => {
    setEmployeeToDelete(id);
    setIsModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsModalOpen(false);
    setEmployeeToDelete(null);
  };

  const confirmDelete = async () => {
    if (EmployeeToDelete) {
      await handleDeleteEmployee(EmployeeToDelete); // Call your existing delete function
    }
    closeDeleteModal();
  };


  const handlelevelChange = (level) => {
    if (level === "OP") {
      SetAssignedPeople(["operations"]);
      setAuthLevel("operations");
      setEmpAuthLevel("L4") // Update the selected level
      setSelectedServices("")
      setEmpGroups([]);
    }
    if ((level === "OM")) {
      SetAssignedPeople(["project manager"]);
      setAuthLevel("project manager");
      setEmpAuthLevel("L4") // Update the selected level
      setSelectedServices("")
      setEmpGroups([]);
    }
    if (level === "L3") {
      setEmpGroups([]);
    }
    if ((level !== "OM" && level !== "OP")) {
      setAuthLevel(level); // Update the selected level
      setEmpAuthLevel(level)
    }

    setShowLeveldown(false);

    if (level !== "L3") {
      setEmpGroups([]);
      setShowDropdown(false);
    }
    if (level !== "L4") {
      setShowLeveldownL4Group(false);
    }
  };


  const filterEmployees = useCallback((query) => {
    if (!query) {
      setFilteredEmployees([...l1employees]);
      return;
    }

    const filtered = l1employees.filter((employee) => {
      const designation = getEmpDesignation(employee) || "";
      return (
        (employee.EmpFirstName?.toLowerCase() || "").includes(
          query.toLowerCase()
        ) ||
        (employee.EmpLastName?.toLowerCase() || "").includes(
          query.toLowerCase()
        ) ||
        (employee.EmpPhone || "").includes(query) ||
        (employee.EmpEmail?.toLowerCase() || "").includes(
          query.toLowerCase()
        ) ||
        (employee.EmpAuthLevel?.toLowerCase() || "").includes(
          query.toLowerCase()
        ) ||
        (employee.servicetype || "").includes(query) ||
        designation.toLowerCase().includes(query.toLowerCase()) ||
        getEmployeeGroupNames(employee).some((groupName) =>
          groupName.toLowerCase().includes(query.toLowerCase())
        )
      );
    });

    setFilteredEmployees(filtered);
  }, [l1employees]);

  useEffect(() => {
    setSortedProjects(filteredEmployees);
    setCurrentPage(1);
  }, [filteredEmployees]);

  const onL4ServiceDropdownClick = () => {
    if (isErpL4ManagerOrOps && (!EmpGroups || EmpGroups.length === 0)) {
      enqueueSnackbar("Please select group first", { variant: "warning" });
      return;
    }
    setShowServiceDropdown(!showServiceDropdown);
  };

  const handleServiceChange = (service) => {
    setSelectedServices(service);
    setShowServiceDropdown(false);
    if (!isErpL4ManagerOrOps) {
      setEmpGroups([]);
    }
  };

  const sortAlphabetically = (targetToSort, order) => {
    const key = `${targetToSort}-${order}`;
    let sorted = [...filteredEmployees]; // Start with the current filtered list

    switch (key) {
      case "EmpFirstName-az":
        sorted.sort((a, b) => (a.EmpFirstName || "").localeCompare(b.EmpFirstName || ""));
        break;
      case "EmpFirstName-za":
        sorted.sort((a, b) => (b.EmpFirstName || "").localeCompare(a.EmpFirstName || ""));
        break;
      case "EmpLastName-az":
        sorted.sort((a, b) => (a.EmpLastName || "").localeCompare(b.EmpLastName || ""));
        break;
      case "EmpLastName-za":
        sorted.sort((a, b) => (b.EmpLastName || "").localeCompare(a.EmpLastName || ""));
        break;
      case "EmpPhone-az":
        sorted.sort((a, b) => (a.EmpPhone || "").localeCompare(b.EmpPhone || ""));
        break;
      case "EmpPhone-za":
        sorted.sort((a, b) => (b.EmpPhone || "").localeCompare(a.EmpPhone || ""));
        break;
      case "EmpEmail-az":
        sorted.sort((a, b) => (a.EmpEmail || "").localeCompare(b.EmpEmail || ""));
        break;
      case "EmpEmail-za":
        sorted.sort((a, b) => (b.EmpEmail || "").localeCompare(a.EmpEmail || ""));
        break;
      case "EmpAuthLevel-az":
        sorted.sort((a, b) => (a.EmpAuthLevel || "").localeCompare(b.EmpAuthLevel || ""));
        break;
      case "EmpAuthLevel-za":
        sorted.sort((a, b) => (b.EmpAuthLevel || "").localeCompare(a.EmpAuthLevel || ""));
        break;
      case "EmpGroups-az":
        sorted.sort((a, b) => {
          const aGroups = getEmployeeGroupNames(a).join(", ");
          const bGroups = getEmployeeGroupNames(b).join(", ");
          return aGroups.localeCompare(bGroups);
        });
        break;
      case "EmpGroups-za":
        sorted.sort((a, b) => {
          const aGroups = getEmployeeGroupNames(a).join(", ");
          const bGroups = getEmployeeGroupNames(b).join(", ");
          return bGroups.localeCompare(aGroups);
        });
        break;
      case "servicetype-az":
        sorted.sort((a, b) => (a.servicetype || "").localeCompare(b.servicetype || ""));
        break;
      case "servicetype-za":
        sorted.sort((a, b) => (b.servicetype || "").localeCompare(a.servicetype || ""));
        break;
      default:
        // Default to original filtered order
        sorted = [...filteredEmployees];
        break;
    }

    setSortedProjects(sorted);
    setCurrentPage(1); // Reset to first page after sorting
  };

  let allowedCountries = [];

  if (directorEmp?.countrycode === "+1") {
    allowedCountries = ["us"];
  } else if (directorEmp?.countrycode === "+91") {
    allowedCountries = ["in"];
  }
  const defaultCountry = allowedCountries.length === 1 ? allowedCountries[0] : allowedCountries[0];
  console.log("allowedCountries", allowedCountries)
  console.log("allowedCountries defaultCountry", defaultCountry)

  useEffect(() => {
    const handleScroll = () => {
      setOpenDropdownId(null);
    };

    // Get the table container element
    const tableContainer = document.querySelector('.maintable'); // Escape the class name

    // Add scroll listener to window
    window.addEventListener("scroll", handleScroll);

    // Add scroll listener to table container if it exists
    if (tableContainer) {
      tableContainer.addEventListener("scroll", handleScroll);
    }

    return () => {
      // Clean up both listeners
      window.removeEventListener("scroll", handleScroll);

      if (tableContainer) {
        tableContainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);
  // Build payload for backend: [{ featureName, accessLevel, saveForFuture }]
  // For every role (L0, L1, L2, L3, L4): send exactly the access list shown on screen for that role.
  // Always include every feature in that role's list with its access level (including "No Access"); never skip or send empty.
  const buildFeatureAccessPayload = () => {
    const payload = [];
    // Build role-based payload based on what is currently shown in the UI.
    const structure = getRolePermissionStructure(AuthLevel);
    const selectedFeatureNameSet = new Set(
      (Array.isArray(accessList) ? accessList : []).map((name) =>
        String(name || "").trim().toUpperCase()
      )
    );
    const graphOnlyModuleIds = new Set([
      "potential-status",
      "probability-status",
      "lead-source",
      "historical-data",
      "lead-conversion-rate",
      "revenue-distribution",
    ]);
    const resolveAccessLevelForSubFeature = (subFeature) => {
      const explicitAccess = featureAccessLevels[subFeature.id];
      if (explicitAccess !== undefined && explicitAccess !== null && String(explicitAccess).trim()) {
        return normalizeAccessLevelValue(explicitAccess);
      }
      const canonicalMappedName = String(subFeature.mappedFeature || "")
        .trim()
        .toUpperCase();
      if (canonicalMappedName && selectedFeatureNameSet.has(canonicalMappedName)) {
        return "Full Access";
      }
      return "No Access";
    };

    const pushIfMissing = (featureName, accessLevel, saveForFuture) => {
      const name = String(featureName || "").trim();
      if (!name) return;
      if (payload.some((p) => String(p.featureName).trim().toUpperCase() === name.toUpperCase())) {
        return;
      }
      payload.push({
        featureName: name,
        accessLevel: normalizeAccessLevelValue(accessLevel),
        saveForFuture: saveForFuture === "Yes",
      });
    };

    structure.forEach((feature) => {
      const moduleGraphSub = feature.subFeatures?.find(
        (sf) => String(sf.name || "").toLowerCase() === "graphs"
      );
      const graphAccessLevel = moduleGraphSub
        ? resolveAccessLevelForSubFeature(moduleGraphSub)
        : "No Access";

      feature.subFeatures.forEach((subFeature) => {
        // For graph-only modules: if Graphs is allowed, then Download/Filter are implicitly allowed.
        const isImplicit =
          graphOnlyModuleIds.has(feature.id) &&
          moduleGraphSub &&
          subFeature.id !== moduleGraphSub.id;

        const accessLevel = isImplicit
          ? graphAccessLevel
          : resolveAccessLevelForSubFeature(subFeature);

        const saveForFuture = featureSaveForFuture[subFeature.id] || "No";

        // Always send every feature with its access level (including "No Access") so the backend
        // stores the full permission set. Never skip "No Access"; backend gets role-specific list only.
        payload.push({
          featureName: subFeature.mappedFeature,
          accessLevel: normalizeAccessLevelValue(accessLevel),
          saveForFuture: saveForFuture === "Yes",
        });

        // Lead Reports Filter is not shown as an independent UI permission for Admin.
        // It is always synced with Lead Reports.
        if (
          feature.id === "lead-tracker" &&
          subFeature.id === LEAD_REPORTS_FEATURE_ID
        ) {
          pushIfMissing("LEAD REPORTS FILTER", accessLevel, saveForFuture);
        }

        // Hardware features: Settings should expand automatically.
        if (feature.id === "hardware-features") {
          if (subFeature.id === "hardware-features-settings") {
            // If Settings is granted, also grant rest of Assembly + Hardware parts features.
            [
              "ASSEMBLY EDIT ASSEMBLY",
              "ASSEMBLY DOWNLOAD ASSEMBLY",
              "ASSEMBLY UPLOAD ASSEMBLY",
              "HARDWARE PARTS ADD HARDWARE PARTS",
              "HARDWARE PARTS EDIT HARDWARE PARTS",
              "HARDWARE PARTS DOWNLOAD HARDWARE PARTS",
              "HARDWARE PARTS UPLOAD HARDWARE PARTS",
            ].forEach((fName) => pushIfMissing(fName, accessLevel, saveForFuture));
          }
        }

        // Sales Settings: make each setting expand to its related permissions automatically.
        if (feature.id === "sales-settings") {
          if (subFeature.id === "sales-settings-client-ratings") {
            pushIfMissing("CLIENT RATINGS DOWNLOAD CLIENT RATING", accessLevel, saveForFuture);
          }
          if (subFeature.id === "sales-settings-add-member") {
            ["ADD MEMBER EDIT MEMBER", "ADD MEMBER DELETE MEMBER"].forEach((fName) =>
              pushIfMissing(fName, accessLevel, saveForFuture)
            );
          }
          if (subFeature.id === "sales-settings-hourly-rates-expenses") {
            ["SALES HOURLY RATES EDIT HOURLY RATES", "SALES HOURLY RATES DELETE HOURLY RATES"].forEach((fName) =>
              pushIfMissing(fName, accessLevel, saveForFuture)
            );
          }
          if (subFeature.id === "sales-settings-ga-tax-rates") {
            pushIfMissing("TAX RATES TAX", accessLevel, saveForFuture);
          }
          if (subFeature.id === "sales-settings-quotation-template") {
            ["QUOTATION TEMPLATE EDIT QUOTATION TEMPLATE", "QUOTATION TEMPLATE DOWNLOAD QUOTATION TEMPLATE"].forEach((fName) =>
              pushIfMissing(fName, accessLevel, saveForFuture)
            );
          }
        }

        // Revenue Distribution: if granted, Top 10 Client and Revenue should be granted automatically.
        if (feature.id === "revenue-distribution") {
          const isGraphsRow = String(subFeature.name || "").toLowerCase() === "graphs";
          if (isGraphsRow) {
            [
              "TOP 10 CLIENT GRAPHS",
              "TOP 10 CLIENT DOWNLOAD",
              "TOP 10 CLIENT FILTER",
              "REVENUE GRAPHS",
              "REVENUE DOWNLOAD",
              "REVENUE FILTER",
            ].forEach((fName) => pushIfMissing(fName, accessLevel, saveForFuture));
          }
        }
      });
    });

    console.log("Sending featureAccess payload to backend:", payload);
    return payload;
  };

  return (
    <>
      <div className=" bg-[#f3f8f8] pb-6">
        <ConfirmationModal
          isOpen={isModalOpen}
          onClose={closeDeleteModal}
          onConfirm={confirmDelete}
        />
        {isLoading && (
          <AdminLoader isLoading={isLoading} />
        )}

        {SuperUser !== "L1" && (
        <div className="flex justify-center align-middle items-center pt-2">
          <div className="w-[90%] bg-white rounded-md py-2">
            {/* <div className='flex gap-2 px-3 py-3 align-middle items-center'>
              <div className='bg-[#e8f0fe] rounded-md text-[#0f61e6] p-2'>
                <PermIdentityOutlinedIcon />
              </div>
              <div>
                <h2 className='font-semibold '>Add Employee</h2>
                <p className='text-xs'>Basic details about Employee</p>
              </div>
            </div> */}

            <div className="mainform px-3">
              <div className='flex gap-2 py-2'>
                <div className='w-[50%]'>
                  <div className='flex gap-1 pb-2 text-xs align-middle items-center font-semibold'>
                    <PermIdentityOutlinedIcon fontSize='small' className='text-[#0f61e6]' /> First Name <span className="text-red-700 text-xs">*</span>
                  </div>
                  <div>
                    <input
                      value={EmpFirstName}
                      className="border w-full bg-[#fcfcfc] border-[#e5e7eb] rounded-md p-2 overflow-hidden outline-none text-xs"
                      type="text"
                      maxLength={30}
                      placeholder="John"
                      onChange={handleFirstName}

                    />
                  </div>
                </div>
                <div className='w-[50%]'>
                  <div className='flex gap-1 pb-2 text-xs align-middle items-center font-semibold'>
                    <PermIdentityOutlinedIcon fontSize='small' className='text-[#0f61e6]' /> Last Name <span className="text-red-700 text-xs">*</span>
                  </div>
                  <div>
                    <input
                      className="border w-full bg-[#fcfcfc] border-[#e5e7eb] rounded-md p-2 overflow-hidden outline-none text-xs"
                      type="text"
                      maxLength={30}
                      placeholder="smith"
                      value={EmpLastName}
                      onChange={handleLastName}

                    />
                  </div>
                </div>
              </div>

              {/* <div className='flex gap-2 py-3 align-middle items-center'>
                <div className='bg-[#e8f0fe] rounded-md text-[#0f61e6] p-2'>
                  <PermIdentityOutlinedIcon />
                </div>
                <div>
                  <h2 className='font-semibold '>Contact Information</h2>
                  <p className='text-xs'>Primary contact details of Employee</p>
                </div>
              </div> */}

              <div className='flex gap-2 py-2'>
                <div className='w-[50%]'>
                  <div className='flex gap-1 pb-2 text-xs align-middle items-center font-semibold'>
                    <CallOutlinedIcon fontSize='small' className='text-[#0f61e6]' />  Mobile Number <span className="text-red-700 text-xs">*</span>
                  </div>
                  <div>
                    <PhoneInput
                      country={defaultCountry}
                      value={EmpPhone}
                      disableDropdown={true}
                      countryCodeEditable={false}
                      disableCountryGuess={true}
                      onChange={(phone) => setEmpPhone(phone)}
                      onlyCountries={allowedCountries}
                      preferredCountries={[]}
                      enableSearch={true}
                      inputProps={{
                        required: true,
                        className:
                          "w-full border text-xs bg-[#fcfcfc] border-[#e5e7eb] lg:pl-12 cp:pl-6 outline-0 rounded overflow-hidden  p-2",
                        style: {
                          fontFamily:
                            "'Trebuchet MS', 'Lucida Sans Unicode', 'Lucida Grande', 'Lucida Sans', Arial, sans-serif",
                        },
                      }}
                    />
                  </div>
                </div>
                <div className='w-[50%]'>
                  <div className='flex gap-1 pb-2 text-xs align-middle items-center font-semibold'>
                    <EmailOutlinedIcon fontSize='small' className='text-[#0f61e6]' /> Email ID <span className="text-red-700 text-xs">*</span>
                  </div>
                  <div>
                    <input
                      className="border w-full bg-[#fcfcfc] border-[#e5e7eb] rounded-md p-2 overflow-hidden outline-none text-xs"
                      type="text"
                      placeholder="hello@employee.com"
                      value={EmpEmail}
                      onChange={handleEmail}

                    />
                  </div>
                </div>
              </div>

              <div className='flex gap-2 py-2'>
                <div className='w-[50%]'>
                  <div className='flex gap-1 pb-2 text-xs align-middle items-center font-semibold'>
                    <LockOutlinedIcon fontSize='small' className='text-[#0f61e6]' /> Authorization Level <span className="text-red-700 text-xs">*</span>
                  </div>
                  <div className=" text-xs ">
                    <div
                      ref={dropdownRefLevel}
                      className="relative flex -pb-4 border bg-[#fcfcfc] border-[#e5e7eb] rounded-md align-middle items-center"
                    >
                      <ExpandMoreIcon
                        color="white"
                        className="absolute cursor-pointer right-0 m-auto bg-[#fcfcfc] rounded-r-md"
                        onClick={() => setShowLeveldown(!showleveldown)}
                      />

                      <button
                        type="button"
                        onClick={() => setShowLeveldown(!showleveldown)}
                        className=" bg-[#fcfcfc] text-left text-nowrap overflow-hidden rounded-md outline-none p-2 w-full"
                      >
                        {
                          AuthLevel
                            ? AuthLevel === "L0"
                              ? "Admin" :
                              AuthLevel === "L3"
                                ? "Sales & PreSales Dept"
                                : AuthLevel === "L2"
                                  ? "Finance Dept"
                                  : AuthLevel === "L5"
                                    ? "Accounting Dept"
                                  : AuthLevel === "L1"
                                    ? "Director"
                                    : AuthLevel === "project manager"
                                      ? "Manager" : AuthLevel === "operations"
                                        ? "Operations Person" : "Select Authorization Level"
                            : "Select Authorization Level"
                        }
                      </button>

                      {showleveldown && (
                        <div
                          style={{ zIndex: 9999 }}
                          className="absolute top-full p-1 left-0 w-full cursor-pointer max-h-96 overflow-y-auto bg-gray-100 text-sm border border-gray-300 shadow-lg flex flex-col gap-2"
                        >
                          {SuperUser !== "L0" && (
                            <div
                              className=""
                              onClick={() => handlelevelChange("L0")}
                            >
                              Admin
                            </div>
                          )}
                          <div
                            className=""
                            onClick={() => handlelevelChange("L1")}
                          >
                            Director/CEO/VP
                          </div>
                          <div onClick={() => handlelevelChange("L2")}>Finance Dept</div>
                          <div onClick={() => handlelevelChange("L5")}>Accounting Dept</div>
                          <div onClick={() => handlelevelChange("L3")}>Sales & PreSales Dept</div>
                          {subscriptionPackage !== "CRM" && (
                            <>
                              <div onClick={() => handlelevelChange("OM")}>
                                Manager
                              </div>
                              <div onClick={() => handlelevelChange("OP")}>
                                Operations Person
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className='w-[50%]'>
                  <div className="w-full text-xs">

                    {(AuthLevel === "project manager" || AuthLevel === "operations") && (
                      <>
                        {subscriptionPackage === "ERP" ? (
                          <div className="w-full">
                            <div className='flex gap-1 pb-2 text-xs align-middle items-center font-semibold'>
                              <GroupsOutlinedIcon fontSize='small' className='text-[#0f61e6]' /> Assign Group(s) <span className="text-red-700 text-xs">*</span>
                            </div>
                            <div
                              className="relative border w-full bg-[#fcfcfc] border-[#e5e7eb] rounded-md shadow-sm p-2"
                              ref={dropdownRefLevelL4Group}
                            >
                              <button
                                type="button"
                                onClick={() =>
                                  setShowLeveldownL4Group(!showleveldownL4Group)
                                }
                                className=" text-left w-full overflow-hidden bg-[#fcfcfc] focus:outline-none text-nowrap"
                              >
                                {EmpGroups.length > 0
                                  ? EmpGroups.join(", ")
                                  : "Select Group(s)"}{" "}
                              </button>
                              <ExpandMoreIcon
                                color="white"
                                className="absolute cursor-pointer top-1 right-0  bg-[#fcfcfc] rounded-r-md"
                                onClick={() =>
                                  setShowLeveldownL4Group(!showleveldownL4Group)
                                }
                              />

                              {showleveldownL4Group && (
                                <div
                                  style={{ zIndex: 9999 }}
                                  className="absolute left-0 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto mt-2"
                                >
                                  {editingEmployee ? (
                                    <>
                                      {[...GroupNames]
                                        .sort((a, b) => {
                                          const aSel = EmpGroups.includes(a.newGroupName);
                                          const bSel = EmpGroups.includes(b.newGroupName);
                                          if (aSel && !bSel) return -1;
                                          if (!aSel && bSel) return 1;
                                          return 0;
                                        })
                                        .map((group, index) => (
                                          <label
                                            key={group.id ?? index}
                                            className="flex items-center px-4 py-2 hover:bg-gray-100 cursor-pointer "
                                          >
                                            <input
                                              type="checkbox"
                                              value={group.newGroupName}
                                              checked={EmpGroups.includes(group.newGroupName)}
                                              onChange={(e) => {
                                                const selectedValue = e.target.value;
                                                if (e.target.checked) {
                                                  setEmpGroups((prev) => [...prev, selectedValue]);
                                                } else {
                                                  setEmpGroups((prev) =>
                                                    prev.filter((g) => g !== selectedValue)
                                                  );
                                                }
                                              }}
                                              className="mr-2"
                                            />
                                            {group.newGroupName}
                                          </label>
                                        ))}
                                    </>
                                  ) : (
                                    <>
                                      {l4AddModeGroupsForPicker.map((group) => (
                                          <label
                                            key={group.id}
                                            className="flex items-center px-4 py-2 hover:bg-gray-100 cursor-pointer"
                                          >
                                            <input
                                              type="checkbox"
                                              value={group.newGroupName}
                                              checked={EmpGroups.includes(group.newGroupName)}
                                              onChange={(e) => {
                                                const selectedValue = e.target.value;
                                                if (e.target.checked) {
                                                  setEmpGroups((prev) => [...prev, selectedValue]);
                                                } else {
                                                  setEmpGroups((prev) =>
                                                    prev.filter((g) => g !== selectedValue)
                                                  );
                                                }
                                              }}
                                              className="mr-2"
                                            />
                                            {group.newGroupName}
                                          </label>
                                        ))}
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        ) : AuthLevel === "project manager" ? (
                          <div className="w-full">
                            <div className='flex gap-1 pb-2 text-xs align-middle items-center font-semibold'>
                              <GroupsOutlinedIcon fontSize='small' className='text-[#0f61e6]' /> Assign Group(s) <span className="text-red-700 text-xs">*</span>
                            </div>
                            <div
                              className="relative border w-full bg-[#fcfcfc] border-[#e5e7eb] rounded-md shadow-sm p-2"
                              ref={dropdownRefLevelL4Group}
                            >
                              <button
                                type="button"
                                onClick={() =>
                                  setShowLeveldownL4Group(!showleveldownL4Group)
                                }
                                className=" text-left w-full overflow-hidden bg-[#fcfcfc] focus:outline-none text-nowrap"
                              >
                                {EmpGroups.length > 0
                                  ? EmpGroups.join(", ")
                                  : "Select Group(s)"}{" "}
                              </button>
                            <ExpandMoreIcon
                              color="white"
                              className="absolute cursor-pointer top-1 right-0  bg-[#fcfcfc] rounded-r-md"
                              onClick={() =>
                                setShowLeveldownL4Group(!showleveldownL4Group)
                              }
                            />

                            {showleveldownL4Group && (
                              <div
                                style={{ zIndex: 9999 }}
                                className="absolute left-0 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto mt-2"
                              >
                                {[...GroupNames]
                                  .sort((a, b) => {
                                    const aSel = EmpGroups.includes(a.newGroupName);
                                    const bSel = EmpGroups.includes(b.newGroupName);
                                    if (aSel && !bSel) return -1;
                                    if (!aSel && bSel) return 1;
                                    return 0;
                                  })
                                  .map((grp, index) => (
                                    <label
                                      key={grp.id ?? index}
                                      className="flex items-center px-4 py-2 hover:bg-gray-100 cursor-pointer "
                                    >
                                      <input
                                        type="checkbox"
                                        value={grp.newGroupName}
                                        checked={EmpGroups.includes(grp.newGroupName)}
                                        onChange={(e) => {
                                          const selectedValue = e.target.value;
                                          if (e.target.checked) {
                                            setEmpGroups((prev) => [...prev, selectedValue]);
                                          } else {
                                            setEmpGroups((prev) =>
                                              prev.filter((g) => g !== selectedValue)
                                            );
                                          }
                                        }}
                                        className="mr-2"
                                      />
                                      {grp.newGroupName}
                                    </label>
                                  ))}
                              </div>
                            )}
                          </div>
                        </div>
                        ) : null}
                      </>
                    )}

                    {AuthLevel === "L3" && (
                      <div className="">
                        <div className="w-full">

                          <div className='flex gap-1 pb-2 text-xs align-middle items-center font-semibold'>
                            <GroupsOutlinedIcon fontSize='small' className='text-[#0f61e6]' /> Assign Group(s) <span className="text-red-700 text-xs">*</span>
                          </div>
                          <div
                            className="relative border w-full bg-[#fcfcfc] border-[#e5e7eb] rounded-md  p-2"
                            ref={dropdownRef}
                          >
                            <button
                              type="button"
                              onClick={() => setShowDropdown(!showDropdown)}
                              className="w-full text-left bg-[#fcfcfc] focus:outline-none overflow-hidden text-nowrap"
                            >
                              {EmpGroups.length > 0
                                ? EmpGroups.join(", ") // Display selected groups
                                : "Select Group(s)"}{" "}
                            </button>

                            <ExpandMoreIcon
                              color="white"
                              className="absolute cursor-pointer top-1 right-0  bg-[#fcfcfc] rounded-r-md"
                              onClick={() => setShowDropdown(!showDropdown)}
                            />

                            {showDropdown && (
                              <div
                                style={{ zIndex: 9999 }}
                                className="bg-white absolute left-0 w-full  border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto mt-2">
                                {editingEmployee ? (
                                  <>
                                    {/* Selected groups at top, then remaining groups */}
                                    {[...GroupNames]
                                      .sort((a, b) => {
                                        const aSel = EmpGroups.includes(a.newGroupName);
                                        const bSel = EmpGroups.includes(b.newGroupName);
                                        if (aSel && !bSel) return -1;
                                        if (!aSel && bSel) return 1;
                                        return 0;
                                      })
                                      .map((group, index) => (
                                      <label
                                        key={group.id ?? index}
                                        className="flex items-center px-4 py-2 hover:bg-gray-100 cursor-pointer "
                                      >
                                        <input
                                          type="checkbox"
                                          value={group.newGroupName}
                                          checked={EmpGroups.includes(group.newGroupName)}
                                          onChange={(e) => {
                                            const selectedValue = e.target.value;
                                            if (e.target.checked) {
                                              setEmpGroups((prev) => [...prev, selectedValue]);
                                            } else {
                                              setEmpGroups((prev) =>
                                                prev.filter((g) => g !== selectedValue)
                                              );
                                            }
                                          }}
                                          className="mr-2"
                                        />
                                        {group.newGroupName}
                                      </label>
                                    ))}
                                  </>
                                ) : (
                                  <>
                                    {GroupAgainNames.map((group) => (
                                      <label
                                        key={group.id}
                                        className="flex bg-white items-center px-4 py-2 hover:bg-gray-100 cursor-pointer"
                                      >
                                        <input
                                          type="checkbox"
                                          value={group.newGroupName}
                                          checked={EmpGroups.includes(
                                            group.newGroupName
                                          )}
                                          onChange={(e) => {
                                            const selectedValue = e.target.value;
                                            if (e.target.checked) {
                                              setEmpGroups((prev) => [
                                                ...prev,
                                                selectedValue,
                                              ]);
                                            } else {
                                              setEmpGroups((prev) =>
                                                prev.filter(
                                                  (group) => group !== selectedValue
                                                )
                                              );
                                            }
                                          }}
                                          className="mr-2"
                                        />
                                        {group.newGroupName}
                                      </label>
                                    ))}
                                  </>
                                )}

                              </div>
                            )}
                          </div>
                        </div>

                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className='flex gap-2 py-2'>
                <div className='w-[50%]'>
                  <div className="flex justify-start w-full">
                    {(AuthLevel === "project manager" || AuthLevel === "operations") && (
                      <div className="w-full text-xs">
                        <div className='flex gap-1 pb-2 text-xs align-middle items-center font-semibold'>
                          <EngineeringOutlinedIcon fontSize='small' className='text-[#0f61e6]' /> Type of Service <span className="text-red-700 text-xs">*</span>
                        </div>
                        <div className=" ">
                          <div
                            ref={dropdownRefService}
                            className="relative flex p-0 rounded-md align-middle items-center"
                          >
                            {subscriptionPackage === "ERP" ? (
                              <>
                                <ExpandMoreIcon
                                  color="white"
                                  className="absolute cursor-pointer right-2 m-auto bg-[#fcfcfc]"
                                  onClick={onL4ServiceDropdownClick}
                                />
                                <button
                                  type="button"
                                  onClick={onL4ServiceDropdownClick}
                                  className="border w-full bg-[#fcfcfc] border-[#e5e7eb] rounded-md text-nowrap p-2 text-start overflow-hidden outline-none"
                                >
                                  {servicetype?.length > 0 ? (
                                    <div className="flex overflow-hidden gap-1">
                                      {servicetype}
                                    </div>
                                  ) : (
                                    <span className="">Select Service Type</span>
                                  )}
                                </button>

                                {showServiceDropdown && (
                                  <div
                                    style={{ zIndex: 9999 }}
                                    className="absolute top-full left-0 p-1 w-full cursor-pointer max-h-96 overflow-y-auto bg-white text-sm border border-gray-300 shadow-lg flex flex-col gap-1"
                                  >
                                    {(erpL4ServiceTypesFromGroups).map((serviceType) => {
                                      const isSelected = servicetype === serviceType;
                                      return (
                                        <div
                                          key={serviceType}
                                          className={`p-1 cursor-pointer ${isSelected
                                            ? "bg-blue-100 font-semibold"
                                            : "hover:bg-gray-200"
                                            }`}
                                          onClick={() =>
                                            handleServiceChange(serviceType)
                                          }
                                        >
                                          {serviceType}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </>
                            ) : (
                              <>
                                <ExpandMoreIcon
                                  color="white"
                                  className="absolute cursor-pointer right-2 m-auto bg-[#fcfcfc]"
                                  onClick={onL4ServiceDropdownClick}
                                />
                                <button
                                  type="button"
                                  onClick={onL4ServiceDropdownClick}
                                  className="border w-full bg-[#fcfcfc] border-[#e5e7eb] rounded-md text-nowrap p-2 text-start overflow-hidden outline-none"
                                >
                                  {servicetype?.length > 0 ? (
                                    <div className="flex overflow-hidden gap-1">
                                      {servicetype}
                                    </div>
                                  ) : (
                                    <span className="">Select Service Type</span>
                                  )}
                                </button>

                                {showServiceDropdown && (
                                  <div
                                    style={{ zIndex: 9999 }}
                                    className="absolute top-full left-0 p-1 w-full cursor-pointer max-h-96 overflow-y-auto bg-white text-sm border border-gray-300 shadow-lg flex flex-col gap-1"
                                  >
                                    {(serviceTypesRedux || []).map((st) => {
                                      const serviceType = st.ServiceType;
                                      const isSelected = servicetype === serviceType;
                                      return (
                                        <div
                                          key={serviceType}
                                          className={`p-1 cursor-pointer ${isSelected
                                            ? "bg-blue-100 font-semibold"
                                            : "hover:bg-gray-200"
                                            }`}
                                          onClick={() =>
                                            handleServiceChange(serviceType)
                                          }
                                        >
                                          {serviceType}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className='w-[50%]'>

                </div>
              </div>

              {(AuthLevel === "L0" ||
                AuthLevel === "L1" ||
                AuthLevel === "project manager" ||
                AuthLevel === "operations" ||
                AuthLevel === "L3" ||
                AuthLevel === "L2" ||
                AuthLevel === "L5") && (
                  <div className=" mt-2 text-xs">
                    <div className={`accesslist w-full border border-gray-300 rounded-md p-4 shadow-sm bg-white`}>
                      <div className="flex justify-between items-center mb-4">
                        <div className="flex gap-2 items-center">
                          <ShieldIcon className="text-[#0f61e6]" />
                          <div>
                            <h3 className="font-semibold text-sm">Role & Permissions</h3>
                            <p className="text-xs text-gray-500">
                              {AuthLevel === "L0" ? "Admin feature access list" : AuthLevel === "L1" ? "Director feature access list" : (AuthLevel === "L2" || AuthLevel === "L5") ? "finance person feature access for each department" : AuthLevel === "L3" ? "Sales person feature access list" : "Manage feature access for each department"}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <label className="flex items-center gap-1.5 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={(() => {
                                const structure = getRolePermissionStructure(AuthLevel);
                                return structure.length > 0 && structure.every((module) =>
                                  module.subFeatures.every((sf) =>
                                    (featureAccessLevels[sf.id] || "No Access") === "Full Access"
                                  )
                                );
                              })()}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  const structure = getRolePermissionStructure(AuthLevel);
                                  const updatedAccess = { ...featureAccessLevels };
                                  const updatedSave = { ...featureSaveForFuture };
                                  const updatedList = [...accessList];
                                  structure.forEach((module) => {
                                    module.subFeatures.forEach((sf) => {
                                      updatedAccess[sf.id] = "Full Access";
                                      updatedSave[sf.id] = "Yes";
                                      if (!updatedList.includes(sf.mappedFeature)) {
                                        updatedList.push(sf.mappedFeature);
                                      }
                                    });
                                  });
                                  setFeatureAccessLevels(updatedAccess);
                                  setFeatureSaveForFuture(updatedSave);
                                  setAccessList(updatedList);
                                }
                              }}
                              className="cursor-pointer accent-[#0f61e6]"
                            />
                            <span className="text-xs font-medium">Select All</span>
                          </label>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium">Role:</span>
                            <select 
                              className="border border-gray-300 rounded-md px-2 py-1 text-xs"
                              value={AuthLevel === "L0" ? "Admin" : AuthLevel === "L1" ? "Director" : AuthLevel === "L3" ? "Sales & Presales" : AuthLevel === "L2" ? "Finance Dept" : AuthLevel === "L5" ? "Accounting Dept" : AuthLevel === "project manager" ? "Manager" : AuthLevel === "operations" ? "Operations Person" : ""}
                              disabled
                            >
                              <option value="Admin">Admin</option>
                              <option value="Director">Director</option>
                              <option value="Sales & Presales">Sales & Presales</option>
                              <option value="Finance Dept">Finance Dept</option>
                              <option value="Accounting Dept">Accounting Dept</option>
                              <option value="Manager">Manager</option>
                              <option value="Operations Person">Operations Person</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <div className="border border-gray-200 rounded-md overflow-hidden">
                        {/* Access list is common for every employee (same as Admin list). */}
                        <>
                          <div className="grid grid-cols-3 bg-gray-50 border-b border-gray-200">
                            <div className="px-4 py-2 font-semibold text-xs border-r border-gray-200">FUNCTIONS</div>
                            <div className="px-4 py-2 font-semibold text-xs border-r border-gray-200">ACCESS LEVEL</div>
                            <div className="px-4 py-2 font-semibold text-xs">SAVE FOR FUTURE</div>
                          </div>
                          {getRolePermissionStructure(AuthLevel).map((module) => {
                              const IconComponent = module.icon;
                              const isExpanded = expandedAdminModules[module.id] !== false;
                              const graphOnlyModuleIds = new Set([
                                "potential-status",
                                "probability-status",
                                "lead-source",
                                "historical-data",
                                "lead-conversion-rate",
                                "revenue-distribution",
                              ]);
                              const visibleSubFeatures = graphOnlyModuleIds.has(module.id)
                                ? module.subFeatures.filter((sf) => String(sf.name || "").toLowerCase() === "graphs")
                                : module.subFeatures;
                              return (
                                <React.Fragment key={module.id}>
                                  {/* Module row - clickable dropdown to show/hide sub-features */}
                                  <div
                                    role="button"
                                    tabIndex={0}
                                    onClick={() =>
                                      setExpandedAdminModules((prev) => ({
                                        ...prev,
                                        [module.id]: !prev[module.id]
                                      }))
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        setExpandedAdminModules((prev) => ({
                                          ...prev,
                                          [module.id]: !prev[module.id]
                                        }));
                                      }
                                    }}
                                    className="grid grid-cols-3 border-b border-gray-200 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                                  >
                                    <div className="px-4 py-3 flex items-center gap-2 border-r border-gray-200 col-span-3">
                                      {isExpanded ? (
                                        <ExpandMoreIcon className="text-[#0f61e6]" fontSize="small" />
                                      ) : (
                                        <ChevronRightIcon className="text-[#0f61e6]" fontSize="small" />
                                      )}
                                      <IconComponent className="text-[#0f61e6]" fontSize="small" />
                                      <span className="text-xs font-semibold">{module.name}</span>
                                    </div>
                                  </div>
                                  {/* Sub-menu rows - with access level and save for future (shown when module expanded) */}
                                  {isExpanded && visibleSubFeatures.map((subFeature) => {
                                    const accessLevel = featureAccessLevels[subFeature.id] || "No Access";
                                    const saveForFuture = featureSaveForFuture[subFeature.id] || "No";
                                    const isLeadReportsFilterRow =
                                      subFeature.id === LEAD_REPORTS_FILTER_FEATURE_ID;
                                    const isLeadReportsNoAccess =
                                      (featureAccessLevels[LEAD_REPORTS_FEATURE_ID] || "No Access") === "No Access";
                                    const isLeadReportsFilterDisabled =
                                      isLeadReportsFilterRow && isLeadReportsNoAccess;
                                    return (
                                      <div
                                        key={subFeature.id}
                                        className={`grid grid-cols-3 border-b border-gray-200 ${isLeadReportsFilterDisabled ? "bg-gray-50 opacity-60" : ""}`}
                                      >
                                        <div className="px-4 py-3 pl-8 flex items-center gap-2 border-r border-gray-200">
                                          <span className="text-xs font-medium">{subFeature.name}</span>
                                        </div>
                                        <div className="px-4 py-3 flex items-center gap-4 border-r border-gray-200">
                                          <label className={`flex items-center gap-1 ${isLeadReportsFilterDisabled ? "cursor-not-allowed" : "cursor-pointer"}`}>
                                            <input
                                              type="radio"
                                              name={`access-${subFeature.id}`}
                                              value="No Access"
                                              checked={accessLevel === "No Access"}
                                              disabled={isLeadReportsFilterDisabled}
                                              onChange={() =>
                                                handleAccessLevelChange(
                                                  subFeature.id,
                                                  subFeature.mappedFeature,
                                                  "No Access"
                                                )
                                              }
                                              className={isLeadReportsFilterDisabled ? "cursor-not-allowed" : "cursor-pointer"}
                                            />
                                            <span className="text-xs">No Access</span>
                                          </label>
                                          <label className={`flex items-center gap-1 ${isLeadReportsFilterDisabled ? "cursor-not-allowed" : "cursor-pointer"}`}>
                                            <input
                                              type="radio"
                                              name={`access-${subFeature.id}`}
                                              value="View Only"
                                              checked={accessLevel === "View Only"}
                                              disabled={isLeadReportsFilterDisabled}
                                              onChange={() =>
                                                handleAccessLevelChange(
                                                  subFeature.id,
                                                  subFeature.mappedFeature,
                                                  "View Only"
                                                )
                                              }
                                              className={isLeadReportsFilterDisabled ? "cursor-not-allowed" : "cursor-pointer"}
                                            />
                                            <span className="text-xs">View Only</span>
                                          </label>
                                          <label className={`flex items-center gap-1 ${isLeadReportsFilterDisabled ? "cursor-not-allowed" : "cursor-pointer"}`}>
                                            <input
                                              type="radio"
                                              name={`access-${subFeature.id}`}
                                              value="Full Access"
                                              checked={accessLevel === "Full Access"}
                                              disabled={isLeadReportsFilterDisabled}
                                              onChange={() =>
                                                handleAccessLevelChange(
                                                  subFeature.id,
                                                  subFeature.mappedFeature,
                                                  "Full Access"
                                                )
                                              }
                                              className={isLeadReportsFilterDisabled ? "cursor-not-allowed" : "cursor-pointer"}
                                            />
                                            <span className="text-xs">Full Access</span>
                                          </label>
                                        </div>
                                        <div className="px-4 py-3 flex items-center gap-4 text-xs text-gray-700">
                                          <label className={`flex items-center gap-1 ${(accessLevel === "No Access" || isLeadReportsFilterDisabled) ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}>
                                            <input
                                              type="radio"
                                              name={`save-${subFeature.id}`}
                                              value="Yes"
                                              checked={saveForFuture === "Yes"}
                                              disabled={accessLevel === "No Access" || isLeadReportsFilterDisabled}
                                              onChange={() => handleSaveForFutureChange(subFeature.id, "Yes")}
                                              className={(accessLevel === "No Access" || isLeadReportsFilterDisabled) ? "cursor-not-allowed" : "cursor-pointer"}
                                            />
                                            <span className="text-xs">Yes</span>
                                          </label>
                                          <label className={`flex items-center gap-1 ${(accessLevel === "Full Access" || accessLevel === "View Only" || isLeadReportsFilterDisabled) ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}>
                                            <input
                                              type="radio"
                                              name={`save-${subFeature.id}`}
                                              value="No"
                                              checked={saveForFuture === "No"}
                                              disabled={accessLevel === "Full Access" || accessLevel === "View Only" || isLeadReportsFilterDisabled}
                                              onChange={() => handleSaveForFutureChange(subFeature.id, "No")}
                                              className={(accessLevel === "Full Access" || accessLevel === "View Only" || isLeadReportsFilterDisabled) ? "cursor-not-allowed" : "cursor-pointer"}
                                            />
                                            <span className="text-xs">No</span>
                                          </label>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </React.Fragment>
                              );
                          })}
                        </>
                      </div>
                    </div>
                  </div>
                )}

              <div className="flex justify-end pt-2">
                <button
                  disabled={
                    !EmpFirstName || !EmpLastName || !EmpPhone || !EmpEmail || (!AuthLevel || !EmpAuthLevel)
                  }
                  onClick={Submit}
                  className={`mx-2 py-2 px-5 ${!EmpFirstName || !EmpLastName || !EmpPhone || !EmpEmail || (!AuthLevel || !EmpAuthLevel)
                    ? "bg-blue-300 cursor-not-allowed"
                    : "bg-[#0f61e6] hover:bg-blue-400"
                    } overflow-hidden shadow-md text-white rounded-lg`}
                >
                  {isEditing ? "Update Employee" : "Add Employee"}
                </button>
                <button
                  onClick={Cancel}
                  className="mx-2 py-2 px-5 border border-[#e5e7eb] bg-[#fafafa]  overflow-hidden rounded-lg"
                >
                  Cancel
                </button>
              </div>

            </div>
          </div>
        </div>
        )}


        <div className="flex justify-center align-middle items-center">
          {(SuperUser === "L1" || showTable) && (
            <div className=" w-[90%] mt-2 p-2 bg-white border border-gray-200 relative overflow-hidden shadow-lg justify-center  rounded-md">
              {/* <div className="flex">
              <h1 className="text-xl w-4/5 mb-2 font-semibold">
                Employee List
              </h1>
              <div className="relative flex w-1/5 justify-end">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  className="border  w-full border-gray-300 rounded-md mb-3 p-2 overflow-hidden shadow-md outline-none "
                  placeholder="Search"
                />
                <MdSearch
                  size={30}
                  className="mt-2 absolute right-1 top-3 transform -translate-y-1/2 text-gray-500"
                />
              </div>
            </div> */}

              <div className="flex justify-between align-middle items-center pb-2">
                <h1 className="text-xl font-semibold">Employee List</h1>
                {/* <div className="flex gap-2 p-1 align-middle items-center bg-[#e0e7ff] rounded-full">
                  <MdSearch className=" w-5 h-5 text-gray-500" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={handleSearchInputChange}
                    placeholder="Search"
                    className="bg-[#e0e7ff] outline-none w-[80%]"
                  />
                </div> */}

                <div className="flex w-60 gap-2 p-1 border border-[#e1e7ef] align-middle items-center rounded-md text-xs">
                  <MdSearch className=" w-5 h-5 text-gray-500" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={handleSearchInputChange}
                    placeholder="Search"
                    className="outline-none w-[80%]"
                  />
                </div>
              </div>
              <hr />
              <hr />
              <div className=" max-h-[550px] overflow-auto maintable">
                <table className="min-w-full border min-h-[10rem]">
                  <thead className="bg-[#e8e8e8] sticky top-0 z-[100]">
                    <tr className="flex font-medium text-xs">
                      <th className="px-1 py-1 w-12 flex justify-between align-middle items-center text-center border capitalize">
                        Sr. No
                      </th>
                      <th className="px-2 py-1 w-28 flex justify-start align-middle items-center text-start border capitalize tracking-wider">
                        <div className="w-[80%]">First Name</div>
                        <div className="w-[20%] flex flex-col justify-end">
                          <div
                            className="cursor-pointer "
                            onClick={() =>
                              sortAlphabetically("EmpFirstName", "az")
                            }
                          >
                            <ArrowDropUp
                              color="white"
                              sx={{ fontSize: "22px" }}
                            />
                          </div>

                          <div
                            className="cursor-pointer -mt-2"
                            onClick={() =>
                              sortAlphabetically("EmpFirstName", "za")
                            }
                          >
                            <ArrowDropDown
                              color="white"
                              sx={{ fontSize: "22px" }}
                            />
                          </div>
                        </div>
                      </th>
                      <th className="px-2 py-1 w-28 flex justify-start align-middle items-center text-start border capitalize tracking-wider">
                        <div className="w-[80%] ">Last Name</div>
                        <div className="w-[20%]  flex flex-col">
                          <div
                            className="cursor-pointer "
                            onClick={() =>
                              sortAlphabetically("EmpLastName", "az")
                            }
                          >
                            <ArrowDropUp
                              color="white"
                              sx={{ fontSize: "22px" }}
                            />
                          </div>

                          <div
                            className="cursor-pointer -mt-2"
                            onClick={() =>
                              sortAlphabetically("EmpLastName", "za")
                            }
                          >
                            <ArrowDropDown
                              color="white"
                              sx={{ fontSize: "22px" }}
                            />
                          </div>
                        </div>
                      </th>
                      <th className=" px-2 py-1  w-32  flex justify-between align-middle items-center text-center border capitalize tracking-wider">
                        <div className="w-[50%] ">Phone</div>
                        <div className="w-[50%] flex flex-col justify-center text-center">
                          <div
                            className="cursor-pointer "
                            onClick={() =>
                              sortAlphabetically("EmpPhone", "az")
                            }
                          >
                            <ArrowDropUp
                              color="white"
                              sx={{ fontSize: "22px" }}
                            />
                          </div>

                          <div
                            className="cursor-pointer -mt-2"
                            onClick={() =>
                              sortAlphabetically("EmpPhone", "za")
                            }
                          >
                            <ArrowDropDown
                              color="white"
                              sx={{ fontSize: "22px" }}
                            />
                          </div>
                        </div>
                      </th>
                      <th className="px-2 py-1 w-60 flex justify-start align-middle items-center border capitalize tracking-wider">
                        <div className="flex w-[70%] justify-start align-middle ">Email</div>
                        <div className="flex w-[30%] flex-col justify-start align-middle items-start">
                          <div
                            className="cursor-pointer "
                            onClick={() =>
                              sortAlphabetically("EmpEmail", "az")
                            }
                          >
                            <ArrowDropUp
                              color="white"
                              sx={{ fontSize: "22px" }}
                            />
                          </div>

                          <div
                            className="cursor-pointer -mt-2"
                            onClick={() =>
                              sortAlphabetically("EmpEmail", "za")
                            }
                          >
                            <ArrowDropDown
                              color="white"
                              sx={{ fontSize: "22px" }}
                            />
                          </div>
                        </div>
                      </th>
                      <th className="px-2 py-1 w-36 flex justify-start align-middle items-center text-start border capitalize tracking-wider">
                        <div className="w-1/2">
                          Designation
                        </div>
                        <div className="w-1/2 flex flex-col pl-2  justify-center items-center align-middle">
                          <div
                            className="cursor-pointer "
                            onClick={() =>
                              sortAlphabetically("EmpAuthLevel", "az")
                            }
                          >
                            <ArrowDropUp
                              color="white"
                              sx={{ fontSize: "22px" }}
                            />
                          </div>

                          <div
                            className="cursor-pointer -mt-2"
                            onClick={() =>
                              sortAlphabetically("EmpAuthLevel", "za")
                            }
                          >
                            <ArrowDropDown
                              color="white"
                              sx={{ fontSize: "22px" }}
                            />
                          </div>
                        </div>
                      </th>
                      <th className="px-2 py-1 w-48 flex justify-start align-middle items-center text-start border capitalize tracking-wider">
                        <div className="w-1/2">Group</div>
                        <div className="w-1/2 text-start flex flex-col">
                          <div
                            className="cursor-pointer "
                            onClick={() =>
                              sortAlphabetically("EmpGroups", "az")
                            }
                          >
                            <ArrowDropUp
                              color="white"
                              sx={{ fontSize: "22px" }}
                            />
                          </div>

                          <div
                            className="cursor-pointer -mt-2"
                            onClick={() =>
                              sortAlphabetically("EmpGroups", "za")
                            }
                          >
                            <ArrowDropDown
                              color="white"
                              sx={{ fontSize: "22px" }}
                            />
                          </div>
                        </div>
                      </th>
                      <th className="px-2 py-1 w-32 flex justify-between align-middle items-center text-start border capitalize tracking-wider">
                        Type Service
                        <div className="flex flex-col">
                          <div
                            className="cursor-pointer "
                            onClick={() =>
                              sortAlphabetically("servicetype", "az")
                            }
                          >
                            <ArrowDropUp
                              color="white"
                              sx={{ fontSize: "22px" }}
                            />
                          </div>

                          <div
                            className="cursor-pointer -mt-2"
                            onClick={() =>
                              sortAlphabetically("servicetype", "za")
                            }
                          >
                            <ArrowDropDown
                              color="white"
                              sx={{ fontSize: "22px" }}
                            />
                          </div>
                        </div>
                      </th>
                      {SuperUser !== "L1" && (
                      <th className="w-16 flex justify-center align-middle items-center text-center border capitalize tracking-wider">
                        Action
                      </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white ">
                    {currentData?.length > 0 ? (
                      currentData?.map((employee, index) => (
                        <tr className="border flex text-xs" key={employee.id}>
                          <td className="px-2 py-1 w-12 text-center flex align-middle items-center">
                            {startIdx + index + 1}
                          </td>
                          <td className="px-2 py-1 w-28 text-ellipsis line-clamp-2 break-all break-words whitespace-normal flex align-middle items-center">
                            {employee.EmpFirstName || ""}
                          </td>
                          <td className=" px-2 py-1 w-28 text-ellipsis line-clamp-2 break-all break-words whitespace-normal flex align-middle items-center">
                            {employee.EmpLastName || " "}
                          </td>
                          <td className=" px-2 py-1 w-32 text-ellipsis line-clamp-2 break-all break-words whitespace-normal flex align-middle items-center">
                            {/* {employee.EmpPhone
                              ? `+${String(employee.EmpPhone).replace(/^(.{2})(.*)$/, "$1 $2")}`
                              : ""} */}
                            {employee.EmpPhone ? (() => {
                              // Get last 10 digits
                              const phoneStr = String(employee?.EmpPhone);
                              const lastTenDigits = phoneStr.slice(-10);

                              // Add country code prefix based on director's countrycode
                              let displayPhone = "";
                              if (directorEmp?.countrycode === "+1") {
                                displayPhone = "1" + lastTenDigits;
                              } else if (directorEmp?.countrycode === "+91") {
                                displayPhone = "91" + lastTenDigits;
                              } else {
                                displayPhone = lastTenDigits;
                              }

                              if (directorEmp?.countrycode === "+1") {
                                return `+${displayPhone.replace(/^(.{1})(.*)$/, "$1 $2")}`;
                              } else if (directorEmp?.countrycode === "+91") {
                                return `+${displayPhone.replace(/^(.{2})(.*)$/, "$1 $2")}`;
                              }

                            })() : ""}

                          </td>
                          <td className="text-[#2563eb] px-2 py-1 w-60 break-words whitespace-normal text-start flex align-middle items-center">
                            {employee.EmpEmail || " "}
                          </td>
                          <td className="px-2 py-1 w-36  text-start break-words whitespace-normal flex align-middle items-center">
                            {getEmpDesignation(employee) || " "}
                          </td>
                          <td className=" px-2 py-1 w-48  text-start break-words whitespace-normal flex align-middle items-center">
                            {getEmployeeGroupNames(employee).length > 0
                              ? getEmployeeGroupNames(employee).join(", ")
                              : " "}{" "}
                          </td>
                          <td className="px-2 py-1 w-32 text-start break-words whitespace-normal flex align-middle items-center">
                            {employee.servicetype ? (
                              <span className="py-1 px-2 rounded-full bg-[#e7f0fe] text-[#0f61e6]"> {employee.servicetype} </span>
                            ) : (
                              <></>
                            )}
                          </td>
                          {SuperUser !== "L1" && (
                          <td
                            className="px-2 py-1 w-16 cursor-pointer text-center"
                          >
                            <div className="px-2 relative">
                              <div
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (openDropdownId !== employee.id) {
                                    setOpenDropdownId(employee.id);
                                  } else {
                                    setOpenDropdownId(null);
                                  }
                                }}
                                className="p-1 cursor-pointer dropdown-toggle-icon"
                              >
                                <EllipsisVertical />
                              </div>
                              {openDropdownId === employee.id && (
                                <div
                                  ref={dropdownsRef}
                                  className={`z-[100] p-1 absolute flex flex-col ${(currentData?.length === 1 ? "top-full" : (index === currentData?.length - 1 || index === currentData?.length - 2) ? 'bottom-full' : 'top-full')} -left-5 bg-white rounded-md shadow border`}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div
                                    onClick={() => {
                                      setOpenDropdownId(null);
                                      editEmployee(employee.id)
                                    }}
                                    className="p-1 flex gap-1 w-full justify-start align-middle items-center border-b cursor-pointer hover:bg-gray-50"
                                  >
                                    <span title="Edit">
                                      <SquarePen size={15} className="text-blue-500" />
                                    </span>
                                    Edit
                                  </div>
                                  <div
                                    onClick={() => {
                                      setOpenDropdownId(null);
                                      openDeleteModal(employee.id)
                                    }}
                                    className="p-1 flex gap-1 w-full justify-start align-middle items-center cursor-pointer hover:bg-gray-50"
                                  >
                                    <span title="Delete">
                                      <Trash2 size={15} className="text-blue-500" />
                                    </span>
                                    Delete
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                          )}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="6"
                          className="border p-4 text-start text-red-500"
                        >
                          {searchQuery
                            ? `No results found for "${searchQuery}"`
                            : "No results found"}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="pagination mt-4 flex justify-between items-center">
                <div>
                  {totalItems > 0 ? (
                    <>
                      Showing {startIdx + 1} to {Math.min(endIdx, totalItems)} of{" "}
                      {totalItems} Entries
                    </>
                  ) : (
                    <>Showing 0 Entries</>
                  )}
                </div>
                {totalPages > 1 && (
                  <div>
                    <button
                      onClick={() => setCurrentPage(1)}
                      className="px-2 py-2 mx-1 border rounded"
                      disabled={currentPage === 1}
                      title="First Page"
                    >
                      <KeyboardDoubleArrowLeftIcon className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                      }
                      className="px-2 py-2 mx-2 border rounded"
                      disabled={currentPage === 1}
                    >
                      <ChevronRightIcon className="w-5 h-5 rotate-180" />

                      {/* Previous */}
                    </button>

                    {Array.from({ length: 3 }, (_, index) => {
                      // Calculate the page number for the current range
                      const pageNumber = currentPage - 1 + index;

                      // Ensure the pageNumber stays within bounds
                      if (pageNumber >= 1 && pageNumber <= totalPages) {
                        return (
                          <button
                            key={pageNumber}
                            onClick={() => setCurrentPage(pageNumber)}
                            className={`px-4 py-2 mx-2 border rounded ${currentPage === pageNumber ? "bg-gray-300" : ""
                              }`}
                          >
                            {pageNumber}
                          </button>
                        );
                      }
                      return null;
                    })}

                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                      }
                      className="px-2 py-2 mx-2 border rounded"
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRightIcon className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      className="px-2 py-2 mx-1 border rounded"
                      disabled={currentPage === totalPages}
                      title="Last Page"
                    >
                      <KeyboardDoubleArrowRightIcon className="w-5 h-5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <br />
        <br />
      </div>
    </>
  );
};

export default Employees;



